import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Link } from 'react-router-dom';
import { ArrowRepeat, ArrowRight, CheckLg, DashLg, GeoAltFill, LightningCharge, PlusLg, Printer, Share, Shop } from 'react-bootstrap-icons';
import StoreSelectorCollection from '../components/StoreSelectorCollection';
import AddressSelector from '../components/AddressSelector';
import { StoreContext } from '../contexts/StoreContext';
import Stores from '../config/stores';
import {
    formatIngredientLabel,
    formatTimeRange,
    getRecipeImageCandidates,
    getRecipeImagePosition,
    normalize
} from '../utils/recipes';
import { fetchJsonOrThrow } from '../utils/fetchJson';
import api_url from '../config/api_url';
import { loadProfile, saveProfile } from '../utils/profileStorage';
import {
    loadPantryState,
    pantryStateToItems,
    savePantryState,
} from '../utils/pantryStorage';
import {
    addShoppingItemToPantryState,
    applyPantryToShoppingGroups,
    consolidateShoppingItemPurchase,
    getPurchasableShoppingGroups,
    summarizeShoppingGroups,
} from '../utils/mealPlanShopping';
import {
    buildWeeklySavingsLabel,
    buildWeeklyShoppingPrice,
    buildNutritionHighlight,
    buildRecommendationReason,
    getSelectedStoreNames,
    selectHomepageRecommendations,
} from '../utils/recommendations';

const PLACEHOLDER_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 420'%3E%3Crect width='640' height='420' fill='%23f4e7d8'/%3E%3Ctext x='50%25' y='50%25' fill='%23845a2f' font-size='28' text-anchor='middle' dominant-baseline='middle'%3EIntet billede%3C/text%3E%3C/svg%3E";

const getRecipeLinkState = (recipe) => ({
    mealPlanRecipeId: normalize(recipe?.recipe_id),
    mealPlanPublicPrice: recipe?.public_price || null,
});

const HOW_IT_WORKS_STEPS = [
    {
        title: 'Lav din madplan',
        text: 'Vælg butikker og retter til den uge, du faktisk skal planlægge.',
        icon: 'Opskrifter.png',
    },
    {
        title: 'Spisekammeret trækkes fra',
        text: 'Listen bruger dine gemte mængder og viser kun det, der mangler.',
        icon: 'Rester.png',
    },
    {
        title: 'Køb resten',
        text: 'Mangler du stadig noget, rundes resten op til pakker, du kan købe.',
        icon: 'Indkøb.png',
    },
    {
        title: 'Gem dine køb',
        text: 'Markér de varer, du købte, og læg dem til de mængder, du allerede har.',
        icon: 'Tilbud.png',
    },
    {
        title: 'Lav maden',
        text: 'Opskriften viser, hvad der bruges fra spisekammeret, før du bekræfter det.',
        icon: 'Mad.png',
    },
    {
        title: 'Gem de faktiske rester',
        text: 'Restmængder gemmes og bliver automatisk regnet med i næste madplan.',
        icon: 'Rester.png',
    },
];

const MAX_PLAN_LENGTH = 7;
const DEFAULT_CUSTOM_PLAN_LENGTH = 5;

const MOBILE_PRIMARY_STORE_TOKENS = ['netto', 'rema1000', 'foetex', 'bilka'];
const MOBILE_STORE_LABELS = {
    Netto: 'Netto',
    Bilka: 'Bilka',
    SuperBrugsen: 'Super Brugsen',
    DagliBrugsen: 'Dagli Brugsen',
    'FÃ¸tex': 'Føtex',
    Kvickly: 'Kvickly',
    Lidl: 'Lidl',
    Meny: 'MENY',
    'MinKÃ¸bmand': 'Min Købmand',
    Rema1000: 'REMA',
    Spar: 'SPAR',
    Coop365: '365',
};

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const getWeeklyPriceAmount = (recipe) => toFiniteNumber(recipe?.public_price?.pris_denne_uge_dkk);

const getWeeklySavingsAmount = (recipe) => toFiniteNumber(recipe?.public_price?.besparelse_dkk);

const formatDkkAmount = (value) => {
    const amount = toFiniteNumber(value);
    return amount === null ? '' : `${Math.round(amount)} kr.`;
};

const formatPlannerPriceLabel = (recipe) => {
    const amount = toFiniteNumber(recipe?.weeklyPriceAmount);
    if (amount !== null) {
        return formatDkkAmount(amount);
    }

    return normalize(recipe?.weeklyPriceLabel).replace(/^Køb ind for\s+/i, '');
};

const formatCompactSavingsLabel = (value) => normalize(value)
    .replace(/\s+i denne uge\.?$/i, '')
    .replace(/\.$/, '');

const formatDishSavingsLabel = (recipe) => {
    const amount = toFiniteNumber(recipe?.weeklySavingsAmount);
    if (amount !== null && amount > 0) {
        return `Spar ${Math.round(amount)} kr`;
    }

    return formatCompactSavingsLabel(recipe?.weeklySavingsLabel);
};

const getCoreIngredientNames = (recipe) => {
    const rolePriority = {
        kerne: 4,
        baerende: 3,
        støtte: 2,
        stoette: 2,
        smagsgiver: 1,
    };

    return (Array.isArray(recipe?.ingredients) ? recipe.ingredients : [])
        .map((ingredient, index) => ({
            name: formatIngredientLabel(ingredient.produkt_navn),
            score:
                (normalize(ingredient.er_kernedriver).toLowerCase() === 'ja' ? 5 : 0) +
                (rolePriority[normalize(ingredient.anbefalingsrolle).toLowerCase()] || 0) +
                Number(ingredient.tilbudsvaegt || 0) -
                index * 0.01,
        }))
        .filter((ingredient) => ingredient.name)
        .sort((left, right) => right.score - left.score || left.name.localeCompare(right.name, 'da-DK'))
        .slice(0, 3)
        .map((ingredient) => ingredient.name);
};

const getOfferStoreNames = (recipe) => {
    const lineItemStores = (Array.isArray(recipe?.public_price?.line_items) ? recipe.public_price.line_items : [])
        .map((item) => normalize(item.tilbud_butik || item.reference_store))
        .filter((storeName) => storeName && normalize(storeName).toLowerCase() !== 'standardpris');
    const matchedOfferStores = (Array.isArray(recipe?.matched_offers) ? recipe.matched_offers : [])
        .map((offer) => normalize(offer.butik))
        .filter(Boolean);

    return Array.from(new Set([...lineItemStores, ...matchedOfferStores]));
};

const getLineItemSavingsAmount = (lineItem) => {
    const weeklyPrice = toFiniteNumber(lineItem?.pris_denne_uge_dkk);
    const normalPrice = toFiniteNumber(lineItem?.normalpris_dkk);

    if (weeklyPrice !== null && normalPrice !== null) {
        return Math.max(0, normalPrice - weeklyPrice);
    }

    const weeklyPackagePrice = toFiniteNumber(lineItem?.tilbudspris_dkk_pr_pakke);
    const normalPackagePrice = toFiniteNumber(lineItem?.normalpris_dkk_pr_pakke);
    const packageCount = toFiniteNumber(lineItem?.pakker) || 1;

    if (weeklyPackagePrice !== null && normalPackagePrice !== null) {
        return Math.max(0, normalPackagePrice - weeklyPackagePrice) * packageCount;
    }

    return 0;
};

const isTruthyValue = (value) => (
    value === true
    || normalize(value).toLowerCase() === 'true'
    || normalize(value).toLowerCase() === 'ja'
    || normalize(value).toLowerCase() === '1'
);

const getPlannerCardKey = (card) => normalize(
    card.diversityGroup
    || card.primaryProtein
    || card.recipeType
    || card.coreIngredientNames?.[0]
    || card.title
).toLowerCase();

const scorePlannerCard = (card, strategyId) => {
    const price = card.weeklyPriceAmount ?? 999;
    const savings = card.weeklySavingsAmount ?? 0;
    const recommendationScore = Number(card.recommendationScore || 0);
    const hasOfferSavings = savings > 0 ? 8 : 0;

    if (strategyId === 'cheap') {
        return 220 - price + savings * 0.25 + recommendationScore * 0.18;
    }

    if (strategyId === 'savings') {
        return savings * 3.4 + recommendationScore * 0.72 - price * 0.08;
    }

    if (strategyId === 'variety') {
        return recommendationScore * 0.94 + savings * 0.65 - price * 0.16 + hasOfferSavings;
    }

    return recommendationScore + savings * 1.55 - price * 0.18 + hasOfferSavings;
};

const rankPlannerCards = (cards, strategyId) => {
    const sortedCards = [...cards].sort((left, right) =>
        scorePlannerCard(right, strategyId) - scorePlannerCard(left, strategyId)
        || (left.weeklyPriceAmount ?? 999) - (right.weeklyPriceAmount ?? 999)
        || normalize(left.title).localeCompare(normalize(right.title), 'da-DK')
    );

    if (strategyId === 'cheap') {
        return sortedCards;
    }

    const preferredCards = [];
    const deferredCards = [];
    const usedKeys = new Set();

    sortedCards.forEach((card) => {
        const key = getPlannerCardKey(card);
        if (key && usedKeys.has(key)) {
            deferredCards.push(card);
            return;
        }

        if (key) {
            usedKeys.add(key);
        }
        preferredCards.push(card);
    });

    return [...preferredCards, ...deferredCards];
};

const selectPlannerCardIds = (cards, targetLength, weeklyBudget) => {
    const uniqueCards = (Array.isArray(cards) ? cards : [])
        .filter((card, index, array) => card?.recipe_id
            && array.findIndex((candidate) => candidate?.recipe_id === card.recipe_id) === index);
    const normalizedBudget = toFiniteNumber(weeklyBudget);

    if (normalizedBudget === null || normalizedBudget <= 0) {
        return uniqueCards.slice(0, targetLength).map((card) => card.recipe_id);
    }

    const selectedIds = [];
    let runningTotal = 0;

    uniqueCards.forEach((card) => {
        if (selectedIds.length >= targetLength) {
            return;
        }

        const cardPrice = toFiniteNumber(card.weeklyPriceAmount);
        if (cardPrice === null || cardPrice <= 0 || runningTotal + cardPrice > normalizedBudget) {
            return;
        }

        selectedIds.push(card.recipe_id);
        runningTotal += cardPrice;
    });

    return selectedIds;
};

const buildReplacementRationale = (currentCard, candidateCard, strategyId) => {
    const currentPrice = toFiniteNumber(currentCard?.weeklyPriceAmount);
    const candidatePrice = toFiniteNumber(candidateCard?.weeklyPriceAmount);
    const currentSavings = toFiniteNumber(currentCard?.weeklySavingsAmount) || 0;
    const candidateSavings = toFiniteNumber(candidateCard?.weeklySavingsAmount) || 0;
    const currentProtein = normalize(currentCard?.primaryProtein).toLowerCase();
    const candidateProtein = normalize(candidateCard?.primaryProtein).toLowerCase();
    const currentKey = currentCard ? getPlannerCardKey(currentCard) : '';
    const candidateKey = candidateCard ? getPlannerCardKey(candidateCard) : '';
    const labels = [];

    if (strategyId === 'cheap' && currentPrice !== null && candidatePrice !== null && currentPrice - candidatePrice >= 8) {
        labels.push('Billigere end valgt');
    }

    if (strategyId === 'savings' && candidateSavings - currentSavings >= 5) {
        labels.push('Sparer mere');
    }

    if (strategyId === 'variety' && candidateKey && candidateKey !== currentKey) {
        labels.push('Mere variation');
    }

    if (currentProtein && candidateProtein && candidateProtein !== currentProtein) {
        labels.push('Anden hovedingrediens');
    }

    if (currentPrice !== null && candidatePrice !== null && currentPrice - candidatePrice >= 8) {
        labels.push('Billigere end valgt');
    }

    if (candidateSavings > 0) {
        labels.push(formatDishSavingsLabel(candidateCard));
    }

    return Array.from(new Set(labels.filter(Boolean))).slice(0, 2);
};

const getResponsivePlanDefaults = () => {
    if (typeof window === 'undefined') {
        return { mode: 'seven', length: MAX_PLAN_LENGTH };
    }

    if (window.matchMedia('(max-width: 575.98px)').matches) {
        return { mode: 'custom', length: 2 };
    }

    if (window.matchMedia('(max-width: 1199.98px)').matches) {
        return { mode: 'custom', length: 3 };
    }

    return { mode: 'seven', length: MAX_PLAN_LENGTH };
};

const getStoreDisplayName = (storeKey) => {
    const configuredName = (Stores[storeKey]?.name || '').trim();

    return configuredName
        || String(storeKey || '')
            .replace(/([a-zA-Z])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim();
};

const normalizeStoreLookup = (value) => normalize(value)
    .toLowerCase()
    .replace(/ã¦/g, 'ae')
    .replace(/ã¸/g, 'oe')
    .replace(/ã¥/g, 'aa')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]/g, '');

const getStoreLookupSlug = (value) => {
    const key = normalize(value).toLowerCase();

    if (key.includes('netto')) return 'netto';
    if (key.includes('bilka')) return 'bilka';
    if (key.includes('super')) return 'superbrugsen';
    if (key.includes('dagli')) return 'daglibrugsen';
    if (key.includes('tex')) return 'foetex';
    if (key.includes('kvickly')) return 'kvickly';
    if (key.includes('lidl')) return 'lidl';
    if (key.includes('meny')) return 'meny';
    if (key.includes('min')) return 'minkoebmand';
    if (key.includes('rema')) return 'rema1000';
    if (key.includes('spar')) return 'spar';
    if (key.includes('coop') || key.includes('365')) return 'coop365';

    return '';
};

const STORE_LOOKUP = Object.keys(Stores).map((storeKey) => ({
    storeKey,
    tokens: [
        storeKey,
        Stores[storeKey]?.name,
        getStoreDisplayName(storeKey),
        getStoreLookupSlug(storeKey),
        getStoreLookupSlug(Stores[storeKey]?.name),
    ].map(normalizeStoreLookup).filter(Boolean),
}));

const getStoreKeyFromName = (value) => {
    const lookup = normalizeStoreLookup(value);

    if (!lookup) {
        return '';
    }

    return STORE_LOOKUP.find(({ tokens }) =>
        tokens.some((token) => token === lookup || lookup.includes(token) || token.includes(lookup))
    )?.storeKey || '';
};

const isStandardPriceStoreName = (storeName) => normalizeStoreLookup(storeName) === 'standardpris';

const getSaleStoreNameFromLineItem = (lineItem) => {
    const directStoreName = normalize(lineItem?.tilbud_butik);
    if (directStoreName && !isStandardPriceStoreName(directStoreName)) {
        return directStoreName;
    }

    const saleCandidate = (Array.isArray(lineItem?.product_candidates) ? lineItem.product_candidates : [])
        .find((candidate) => {
            const candidateStoreName = normalize(candidate?.butik);
            const isSale =
                isTruthyValue(candidate?.is_on_sale)
                || normalize(candidate?.pricing_source).toLowerCase() === 'weekly_offer';

            return candidateStoreName && !isStandardPriceStoreName(candidateStoreName) && isSale;
        });

    return normalize(saleCandidate?.butik);
};

const getStoreSourceMeta = (storeName) => {
    const storeKey = getStoreKeyFromName(storeName);
    const normalizedName = normalize(storeName);
    const fallbackKey = normalizeStoreLookup(normalizedName);

    return {
        key: storeKey || fallbackKey || normalizedName,
        storeKey,
        name: storeKey ? getStoreDisplayName(storeKey) : normalizedName,
        logo: storeKey ? Stores[storeKey]?.logo : null,
    };
};

const getRecipeDiscountSources = (recipe) => {
    const sourceMap = new Map();
    const addSource = (storeName, values = {}) => {
        const meta = getStoreSourceMeta(storeName);
        if (!meta.key || !meta.name) {
            return;
        }

        const currentSource = sourceMap.get(meta.key) || {
            ...meta,
            savingsAmount: 0,
            itemCount: 0,
            hasEstimatedSavings: false,
            hasLineSavings: false,
        };

        currentSource.savingsAmount += values.savingsAmount || 0;
        currentSource.itemCount += values.itemCount || 0;
        currentSource.hasEstimatedSavings = currentSource.hasEstimatedSavings || Boolean(values.hasEstimatedSavings);
        currentSource.hasLineSavings = currentSource.hasLineSavings || Boolean(values.hasLineSavings);
        sourceMap.set(meta.key, currentSource);
    };

    (Array.isArray(recipe?.public_price?.line_items) ? recipe.public_price.line_items : []).forEach((lineItem) => {
        const storeName = getSaleStoreNameFromLineItem(lineItem);
        const savingsAmount = getLineItemSavingsAmount(lineItem);
        const isWeeklyOffer = normalize(lineItem?.pricing_source).toLowerCase() === 'weekly_offer';

        if (!storeName || (savingsAmount <= 0 && !isWeeklyOffer)) {
            return;
        }

        addSource(storeName, {
            savingsAmount,
            itemCount: 1,
            hasLineSavings: savingsAmount > 0,
        });
    });

    if (sourceMap.size === 0) {
        getOfferStoreNames(recipe).forEach((storeName) => {
            addSource(storeName);
        });
    }

    const recipeSavingsAmount = toFiniteNumber(recipe?.weeklySavingsAmount) ?? getWeeklySavingsAmount(recipe) ?? 0;
    const sources = Array.from(sourceMap.values());
    const lineSavingsTotal = sources.reduce((sum, source) => sum + source.savingsAmount, 0);

    if (sources.length > 0 && lineSavingsTotal <= 0 && recipeSavingsAmount > 0) {
        const estimatedSavingsPerSource = recipeSavingsAmount / sources.length;
        sources.forEach((source) => {
            source.savingsAmount = estimatedSavingsPerSource;
            source.hasEstimatedSavings = true;
        });
    }

    return sources.sort((left, right) =>
        right.savingsAmount - left.savingsAmount
        || right.itemCount - left.itemCount
        || left.name.localeCompare(right.name, 'da-DK')
    );
};

const buildPlanDiscountSources = (cards) => {
    const sourceMap = new Map();

    (Array.isArray(cards) ? cards : []).forEach((card) => {
        const recipeSources = getRecipeDiscountSources(card);
        const recipeSavingsAmount = toFiniteNumber(card?.weeklySavingsAmount) || 0;

        recipeSources
            .filter((source) => source.savingsAmount > 0 || source.itemCount > 0 || recipeSavingsAmount > 0)
            .forEach((source) => {
                const currentSource = sourceMap.get(source.key) || {
                    ...source,
                    savingsAmount: 0,
                    itemCount: 0,
                    recipeCount: 0,
                    hasEstimatedSavings: false,
                    hasLineSavings: false,
                };

                currentSource.savingsAmount += source.savingsAmount;
                currentSource.itemCount += source.itemCount;
                currentSource.recipeCount += 1;
                currentSource.hasEstimatedSavings = currentSource.hasEstimatedSavings || source.hasEstimatedSavings;
                currentSource.hasLineSavings = currentSource.hasLineSavings || source.hasLineSavings;
                sourceMap.set(source.key, currentSource);
            });
    });

    return Array.from(sourceMap.values()).sort((left, right) =>
        right.savingsAmount - left.savingsAmount
        || right.itemCount - left.itemCount
        || right.recipeCount - left.recipeCount
        || left.name.localeCompare(right.name, 'da-DK')
    );
};

const getShoppingItemName = (item) => formatIngredientLabel(
    item?.selected_product_name
    || item?.ingredient_family_name
    || item?.produkt_navn
    || 'Vare'
);

const getShoppingItemKey = (item) => normalize(
    item?.selected_product_id
    || [
        item?.ingredient_family_id,
        item?.produkt_navn,
        item?.pakke_stoerrelse,
        item?.pakke_enhed,
    ].filter(Boolean).join('|')
).toLowerCase();

const shouldIncludeFallbackIngredient = (ingredient) => {
    const explicitBasketValue = ingredient?.in_shopping_basket ?? ingredient?.er_i_indkoebskurv;
    const isPantryBasic = isTruthyValue(ingredient?.pantry_basic)
        || isTruthyValue(ingredient?.er_pantry_basic);

    return explicitBasketValue === undefined
        ? !isPantryBasic
        : isTruthyValue(explicitBasketValue);
};

const buildPlanShoppingGroups = (cards) => {
    const groups = new Map();
    const addItem = (rawItem, recipe, isFallback = false) => {
        const saleStoreName = isFallback ? '' : getSaleStoreNameFromLineItem(rawItem);
        const storeMeta = saleStoreName
            ? { ...getStoreSourceMeta(saleStoreName), isOfferStore: true }
            : {
                key: 'other',
                storeKey: '',
                name: 'Valgfri butik',
                logo: null,
                isOfferStore: false,
            };
        const itemKey = `${storeMeta.key}:${getShoppingItemKey(rawItem)}`;
        const packageCount = toFiniteNumber(rawItem?.pakker ?? rawItem?.antal_pakker) || 1;
        const weeklyPrice = toFiniteNumber(rawItem?.pris_denne_uge_dkk);
        const normalPrice = toFiniteNumber(rawItem?.normalpris_dkk);
        const effectiveNormalPrice = normalPrice ?? weeklyPrice;
        const group = groups.get(storeMeta.key) || {
            ...storeMeta,
            itemMap: new Map(),
            weeklyPriceAmount: 0,
            normalPriceAmount: 0,
        };
        const currentItem = group.itemMap.get(itemKey) || {
            key: itemKey,
            name: getShoppingItemName(rawItem),
            packageCount: 0,
            packageSize: normalize(rawItem?.pakke_stoerrelse),
            packageUnit: normalize(rawItem?.pakke_enhed),
            category: normalize(rawItem?.produkt_kategori || rawItem?.ingredient_family_id),
            weeklyPriceAmount: 0,
            normalPriceAmount: 0,
            weeklyPackagePrice: null,
            normalPackagePrice: null,
            recipeNames: new Set(),
            matchCandidates: [],
            requirements: [],
        };

        currentItem.packageCount += packageCount;
        currentItem.weeklyPriceAmount += weeklyPrice || 0;
        currentItem.normalPriceAmount += effectiveNormalPrice ?? 0;
        if (currentItem.weeklyPackagePrice === null && weeklyPrice !== null) {
            currentItem.weeklyPackagePrice = weeklyPrice / packageCount;
        }
        if (currentItem.normalPackagePrice === null && effectiveNormalPrice !== null) {
            currentItem.normalPackagePrice = effectiveNormalPrice / packageCount;
        }
        currentItem.recipeNames.add(normalize(recipe?.title || recipe?.name));
        currentItem.matchCandidates.push(rawItem);
        currentItem.requirements.push({
            amount: rawItem?.adapted_brug_maengde ?? rawItem?.recipe_target_maengde ?? rawItem?.brug_maengde,
            unit: rawItem?.adapted_brug_enhed ?? rawItem?.recipe_target_enhed ?? rawItem?.brug_enhed,
            ingredient: rawItem,
        });
        group.itemMap.set(itemKey, currentItem);
        groups.set(storeMeta.key, group);
    };

    (Array.isArray(cards) ? cards : []).forEach((recipe) => {
        const lineItems = Array.isArray(recipe?.public_price?.line_items)
            ? recipe.public_price.line_items
            : [];

        if (lineItems.length > 0) {
            lineItems.forEach((lineItem) => addItem(lineItem, recipe));
            return;
        }

        (Array.isArray(recipe?.ingredients) ? recipe.ingredients : [])
            .filter(shouldIncludeFallbackIngredient)
            .forEach((ingredient) => addItem(ingredient, recipe, true));
    });

    return Array.from(groups.values())
        .map((group) => {
            const items = Array.from(group.itemMap.values())
                .map((item) => consolidateShoppingItemPurchase({
                    ...item,
                    recipeNames: Array.from(item.recipeNames).filter(Boolean),
                }))
                .sort((left, right) => left.name.localeCompare(right.name, 'da-DK'));
            const weeklyPriceAmount = items.reduce(
                (sum, item) => sum + (item.weeklyPriceAmount || 0),
                0
            );
            const normalPriceAmount = items.reduce(
                (sum, item) => sum + (item.normalPriceAmount || 0),
                0
            );

            return {
                ...group,
                items,
                weeklyPriceAmount,
                normalPriceAmount,
                savingsAmount: Math.max(0, normalPriceAmount - weeklyPriceAmount),
            };
        })
        .sort((left, right) =>
            Number(right.isOfferStore) - Number(left.isOfferStore)
            || left.name.localeCompare(right.name, 'da-DK')
        );
};

const formatShoppingPackage = (item) => {
    const count = toFiniteNumber(item?.packageCount) || 1;
    const formattedCount = Number.isInteger(count)
        ? count.toString()
        : count.toLocaleString('da-DK', { maximumFractionDigits: 2 });
    const packageLabel = count === 1 ? '1 pakke' : `${formattedCount} pakker`;
    const sizeLabel = [item?.packageSize, item?.packageUnit].filter(Boolean).join(' ');

    return sizeLabel ? `${packageLabel} á ${sizeLabel}` : packageLabel;
};

const formatShoppingQuantity = (amount, unit) => {
    const numericAmount = toFiniteNumber(amount);
    if (numericAmount === null) {
        return '';
    }
    const formattedAmount = Number.isInteger(numericAmount)
        ? numericAmount.toString()
        : numericAmount.toLocaleString('da-DK', { maximumFractionDigits: 2 });
    return `${formattedAmount} ${normalize(unit)}`.trim();
};

const formatPantryShoppingAdjustment = (item) => {
    const pantryAmount = toFiniteNumber(item?.pantryUsedAmount);
    if (pantryAmount === null || pantryAmount <= 0) {
        return '';
    }

    const originalCount = toFiniteNumber(item?.originalPackageCount) || 1;
    const packageSize = toFiniteNumber(item?.packageSize);
    const pantryPackageCount = packageSize && pantryAmount % packageSize === 0
        ? pantryAmount / packageSize
        : null;
    if (pantryPackageCount !== null) {
        const neededLabel = originalCount === 1 ? '1 pakke i planen' : `${originalCount} pakker i planen`;
        const pantryLabel = pantryPackageCount === 1
            ? '1 pakke fra spisekammer'
            : `${pantryPackageCount} pakker fra spisekammer`;
        return `${neededLabel} · ${pantryLabel}`;
    }

    return `${formatShoppingQuantity(pantryAmount, item?.pantryUsedUnit)} fra spisekammer`;
};

const buildShoppingListShareText = (groups, { mealCount = 0, totalSavings = 0 } = {}) => {
    const lines = [
        'Indkøbsliste fra Budgetvenlig',
        `${mealCount} ${mealCount === 1 ? 'ret' : 'retter'}${totalSavings > 0 ? ` · Spar ca. ${formatDkkAmount(totalSavings)}` : ''}`,
        '',
    ];

    groups.forEach((group) => {
        lines.push(group.name);
        group.items.forEach((item) => {
            if (item.isFullyCoveredByPantry) {
                return;
            }
            lines.push(`☐ ${item.name} — ${formatShoppingPackage(item)}`);
        });
        lines.push('');
    });

    lines.push('Tilbudsvarer er grupperet efter den valgte butik. Øvrige varer kan købes i en valgfri butik.');
    return lines.join('\n').trim();
};

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    textArea.remove();
    return copied;
};

const uniqueStoreKeys = (storeKeys) => Array.from(new Set(
    (Array.isArray(storeKeys) ? storeKeys : []).filter((storeKey) => Stores[storeKey])
));

const getSnapshotStoreScopeKeys = (snapshot) => uniqueStoreKeys(snapshot?.selectedStores);

const snapshotHasGeoLocation = (snapshot) => Boolean(snapshot?.geoLocation?.lat && snapshot?.geoLocation?.lon);

const criteriaHasGeoLocation = (criteria) => (
    toFiniteNumber(criteria?.geoLocation?.lat) !== null
    && toFiniteNumber(criteria?.geoLocation?.lon) !== null
);

const getLocationLookupKey = (criteria) => {
    if (!criteriaHasGeoLocation(criteria)) {
        return '';
    }

    const lat = toFiniteNumber(criteria.geoLocation.lat);
    const lon = toFiniteNumber(criteria.geoLocation.lon);
    const radius = toFiniteNumber(criteria.radius) || 2;

    return `${lat.toFixed(6)}:${lon.toFixed(6)}:${radius.toFixed(1)}`;
};

const shouldEnforceStoreScope = (snapshot) => Boolean(snapshot)
    && (snapshotHasGeoLocation(snapshot) || getSnapshotStoreScopeKeys(snapshot).length > 0);

const getStoreKeysFromDiscountSources = (sources) => uniqueStoreKeys(
    (Array.isArray(sources) ? sources : [])
        .map((source) => source.storeKey || getStoreKeyFromName(source.name))
        .filter(Boolean)
);

const formatStoreKeyList = (storeKeys, maxVisible = 4) => {
    const names = getSelectedStoreNames(uniqueStoreKeys(storeKeys)).filter(Boolean);

    if (names.length <= maxVisible) {
        return names.join(', ');
    }

    return `${names.slice(0, maxVisible).join(', ')} +${names.length - maxVisible}`;
};

const storeNameIsInScope = (storeName, scopedStoreKeys) => {
    const scope = uniqueStoreKeys(scopedStoreKeys);

    if (scope.length === 0) {
        return true;
    }

    const storeKey = getStoreKeyFromName(storeName);
    return Boolean(storeKey && scope.includes(storeKey));
};

const isWeeklyOfferCandidate = (candidate) => (
    isTruthyValue(candidate?.is_on_sale)
    || normalize(candidate?.pricing_source).toLowerCase() === 'weekly_offer'
);

const getCandidatePackageCount = (candidate) => (
    toFiniteNumber(candidate?.packages) || toFiniteNumber(candidate?.pakker) || 1
);

const getCandidateWeeklyTotal = (candidate) => {
    const total = toFiniteNumber(candidate?.pris_denne_uge_dkk);
    if (total !== null) {
        return total;
    }

    const packagePrice = toFiniteNumber(candidate?.tilbudspris_dkk_pr_pakke);
    return packagePrice === null ? null : packagePrice * getCandidatePackageCount(candidate);
};

const getCandidateNormalTotal = (candidate) => {
    const total = toFiniteNumber(candidate?.normalpris_dkk);
    if (total !== null) {
        return total;
    }

    const packagePrice = toFiniteNumber(candidate?.normalpris_dkk_pr_pakke);
    return packagePrice === null ? getCandidateWeeklyTotal(candidate) : packagePrice * getCandidatePackageCount(candidate);
};

const getProductIdentityKey = (value) => normalize(value)
    .toLocaleLowerCase('da-DK')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getAllowedProductIdentityKeys = (lineItem) => {
    const allowedVariants = typeof lineItem?.allowed_variants === 'string'
        ? lineItem.allowed_variants.split('|')
        : Array.isArray(lineItem?.allowed_variants)
            ? lineItem.allowed_variants
            : [];

    return new Set([
        lineItem?.produkt_navn,
        lineItem?.ingredient_family_name,
        ...allowedVariants,
    ].map(getProductIdentityKey).filter(Boolean));
};

const candidateMatchesLineItem = (lineItem, candidate) => {
    const allowedKeys = getAllowedProductIdentityKeys(lineItem);
    if (allowedKeys.size === 0) {
        return true;
    }

    const candidateKeys = [
        candidate?.produkt_navn,
        candidate?.variant_id,
    ].map(getProductIdentityKey).filter(Boolean);
    const matchStrength = toFiniteNumber(candidate?.match_strength);

    return candidateKeys.length === 0
        || candidateKeys.some((key) => allowedKeys.has(key))
        || (matchStrength !== null && matchStrength >= 0.94);
};

const filterProductCandidatesToScope = (lineItem, candidates, scopedStoreKeys) => {
    const scope = uniqueStoreKeys(scopedStoreKeys);
    const compatibleCandidates = (Array.isArray(candidates) ? candidates : [])
        .filter((candidate) => candidateMatchesLineItem(lineItem, candidate));

    if (scope.length === 0) {
        return compatibleCandidates;
    }

    return compatibleCandidates.filter((candidate) => {
        const storeName = normalize(candidate?.butik);
        return isStandardPriceStoreName(storeName) || storeNameIsInScope(storeName, scope);
    });
};

const chooseScopedLineItemCandidate = (lineItem, scopedStoreKeys) => {
    const scopedCandidates = filterProductCandidatesToScope(
        lineItem,
        lineItem?.product_candidates,
        scopedStoreKeys
    );
    const saleCandidates = scopedCandidates
        .filter((candidate) => isWeeklyOfferCandidate(candidate) && storeNameIsInScope(candidate?.butik, scopedStoreKeys))
        .filter((candidate) => getCandidateWeeklyTotal(candidate) !== null)
        .sort((left, right) =>
            (getCandidateWeeklyTotal(left) ?? 9999) - (getCandidateWeeklyTotal(right) ?? 9999)
            || (getCandidateNormalTotal(right) ?? 0) - (getCandidateNormalTotal(left) ?? 0)
        );

    if (saleCandidates.length > 0) {
        return {
            candidate: saleCandidates[0],
            isSale: true,
            scopedCandidates,
        };
    }

    const standardCandidate = scopedCandidates.find((candidate) => isStandardPriceStoreName(candidate?.butik))
        || scopedCandidates.find((candidate) => !isWeeklyOfferCandidate(candidate));

    return {
        candidate: standardCandidate || null,
        isSale: false,
        scopedCandidates,
    };
};

export const buildScopedLineItem = (lineItem, scopedStoreKeys) => {
    const choice = chooseScopedLineItemCandidate(lineItem, scopedStoreKeys);
    const candidate = choice.candidate;

    if (!candidate) {
        return {
            ...lineItem,
            product_candidates: choice.scopedCandidates,
            tilbud_butik: '',
            pricing_source: 'missing',
            pris_denne_uge_dkk: null,
            normalpris_dkk: null,
            pris_status: 'mangler',
        };
    }

    const weeklyTotal = getCandidateWeeklyTotal(candidate);
    const normalTotal = getCandidateNormalTotal(candidate);
    const storeName = normalize(candidate.butik);

    return {
        ...lineItem,
        product_candidates: choice.scopedCandidates,
        selected_product_id: normalize(candidate.product_id || lineItem?.selected_product_id),
        selected_variant_id: normalize(candidate.variant_id || lineItem?.selected_variant_id),
        selected_product_name: normalize(candidate.produkt_navn_visning || candidate.produkt_navn || lineItem?.selected_product_name),
        selected_brand: normalize(candidate.brand || lineItem?.selected_brand),
        pakker: getCandidatePackageCount(candidate),
        pakke_stoerrelse: normalize(candidate.pakke_stoerrelse || lineItem?.pakke_stoerrelse),
        pakke_enhed: normalize(candidate.pakke_enhed || lineItem?.pakke_enhed),
        adapted_brug_maengde: normalize(candidate.adapted_amount || lineItem?.adapted_brug_maengde),
        adapted_brug_enhed: normalize(candidate.adapted_unit || lineItem?.adapted_brug_enhed),
        package_fit_reason: normalize(candidate.fit_reason || lineItem?.package_fit_reason),
        tilbudspris_dkk_pr_pakke: toFiniteNumber(candidate.tilbudspris_dkk_pr_pakke),
        normalpris_dkk_pr_pakke: toFiniteNumber(candidate.normalpris_dkk_pr_pakke),
        pris_denne_uge_dkk: weeklyTotal,
        normalpris_dkk: normalTotal,
        tilbud_butik: choice.isSale ? storeName : 'Standardpris',
        pricing_source: choice.isSale ? 'weekly_offer' : 'standardpris_fallback',
        pris_status: weeklyTotal === null ? 'mangler' : 'ok',
        pris_note: '',
    };
};

const applyStoreScopeToRecipePricing = (recipe, scopedStoreKeys) => {
    const scope = uniqueStoreKeys(scopedStoreKeys);

    if (scope.length === 0) {
        return recipe;
    }

    const publicPrice = recipe?.public_price || {};
    const originalLineItems = Array.isArray(publicPrice.line_items) ? publicPrice.line_items : [];
    const scopedLineItems = originalLineItems.map((lineItem) => buildScopedLineItem(lineItem, scope));
    const pricedLineItems = scopedLineItems.filter((lineItem) => toFiniteNumber(lineItem.pris_denne_uge_dkk) !== null);
    const totalPrice = pricedLineItems.reduce((sum, lineItem) => sum + (toFiniteNumber(lineItem.pris_denne_uge_dkk) || 0), 0);
    const normalPrice = pricedLineItems.reduce((sum, lineItem) => sum + (toFiniteNumber(lineItem.normalpris_dkk) || 0), 0);
    const matchedOffers = (Array.isArray(recipe?.matched_offers) ? recipe.matched_offers : [])
        .filter((offer) => storeNameIsInScope(offer?.butik, scope));

    return {
        ...recipe,
        matched_offers: matchedOffers,
        public_price: {
            ...publicPrice,
            pris_denne_uge_dkk: pricedLineItems.length > 0 ? Math.round(totalPrice * 100) / 100 : null,
            normalpris_dkk: pricedLineItems.length > 0 ? Math.round(normalPrice * 100) / 100 : null,
            besparelse_dkk: pricedLineItems.length > 0 ? Math.round(Math.max(0, normalPrice - totalPrice) * 100) / 100 : null,
            priced_item_count: pricedLineItems.length,
            unpriced_item_count: scopedLineItems.length - pricedLineItems.length,
            line_items: scopedLineItems,
        },
        scoped_store_keys: scope,
    };
};

const recipeHasScopedDiscountSource = (recipe, scopedStoreKeys) => {
    const scope = uniqueStoreKeys(scopedStoreKeys);

    if (scope.length === 0) {
        return true;
    }

    return getRecipeDiscountSources(recipe).some((source) => {
        const storeKey = source.storeKey || getStoreKeyFromName(source.name);
        return storeKey && scope.includes(storeKey) && (source.itemCount > 0 || source.savingsAmount > 0);
    });
};

const getHeroRecipeStoreMeta = (recipe, fallbackStoreNames = []) => {
    const matchedStoreNames = Array.isArray(recipe?.matchedStoreNames)
        ? recipe.matchedStoreNames
        : [];
    const storeName = normalize(matchedStoreNames[0] || fallbackStoreNames[0]);
    const storeKey = getStoreKeyFromName(storeName);

    return {
        name: storeKey ? getStoreDisplayName(storeKey) : storeName,
        logo: storeKey ? Stores[storeKey]?.logo : null,
    };
};

const getHeroMapStoreLabel = (storeKey) => {
    const slug = getStoreLookupSlug(storeKey) || getStoreLookupSlug(Stores[storeKey]?.name);
    const displayName = getStoreDisplayName(storeKey);

    if (slug === 'rema1000') return 'REMA';
    if (slug === 'foetex') return 'Føtex';

    return displayName;
};

const extractLocationStoreName = (location) => {
    if (typeof location === 'string') {
        return location;
    }

    if (!location || typeof location !== 'object') {
        return '';
    }

    return location.brand
        || location.brand_name
        || location.brandName
        || location.store_brand
        || location.storeBrand
        || location.store_brand_name
        || location.name
        || location.store
        || location.store_name
        || location.butik
        || location.chain
        || location.chain_name
        || location.kæde
        || location.kaede
        || location.company
        || location.title
        || '';
};

const extractLocationCollectionEntries = (collection) => {
    if (Array.isArray(collection)) {
        return collection;
    }

    if (!collection || typeof collection !== 'object') {
        return [];
    }

    return Object.entries(collection).map(([key, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return {
                brand: key,
                ...value,
            };
        }

        return value === true || value === null || value === undefined ? key : value;
    });
};

const extractLocationEntries = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    const directCollection = payload.locations
        || payload.stores
        || payload.brands
        || payload.results;

    if (directCollection) {
        return extractLocationCollectionEntries(directCollection);
    }

    if (payload.data) {
        return extractLocationEntries(payload.data);
    }

    return extractLocationCollectionEntries(payload);
};

const getStoreKeysFromLocationsPayload = (payload) => {
    const matches = extractLocationEntries(payload)
        .map(extractLocationStoreName)
        .map((storeName) => getStoreLookupSlug(storeName) || normalizeStoreLookup(storeName))
        .filter(Boolean)
        .map((storeName) => STORE_LOOKUP.find(({ tokens }) => tokens.some((token) => storeName.includes(token) || token.includes(storeName)))?.storeKey)
        .filter(Boolean);

    return Array.from(new Set(matches));
};

const fetchNearbyStoreKeys = async ({ geoLocation, radius }) => {
    const lat = toFiniteNumber(geoLocation?.lat);
    const lon = toFiniteNumber(geoLocation?.lon);

    if (lat === null || lon === null) {
        return [];
    }

    const params = new URLSearchParams({
        lat: String(lat),
        lon: String(lon),
        radius: String(toFiniteNumber(radius) || 2),
    });
    const payload = await fetchJsonOrThrow(`${api_url}api/locations?${params.toString()}`);

    return uniqueStoreKeys(getStoreKeysFromLocationsPayload(payload));
};

function Home() {
    const heroRef = useRef(null);
    const { stores, setStores } = useContext(StoreContext);
    const savedProfile = useMemo(() => loadProfile(), []);
    const weeklyBudget = toFiniteNumber(savedProfile.weeklyBudget);
    const hasWeeklyBudget = weeklyBudget !== null && weeklyBudget > 0;
    const [searchCriteria, setSearchCriteria] = useState({
        address: savedProfile.address,
        radius: savedProfile.radius,
        geoLocation: savedProfile.geoLocation,
    });
    const [nearbyStoreLookup, setNearbyStoreLookup] = useState({
        criteriaKey: '',
        status: 'idle',
        storeKeys: [],
    });
    const [isAddressSuggestionConfirmed, setIsAddressSuggestionConfirmed] = useState(
        Boolean(savedProfile.address && savedProfile.geoLocation?.lat && savedProfile.geoLocation?.lon)
    );
    const [isMobileAddressOpen, setIsMobileAddressOpen] = useState(false);
    const [isSearchCriteriaPromptVisible, setIsSearchCriteriaPromptVisible] = useState(false);
    const [searchSnapshot, setSearchSnapshot] = useState(null);
    const [recipeRecords, setRecipeRecords] = useState([]);
    const [weeklyRecommendations, setWeeklyRecommendations] = useState(null);
    const [homepageDataStatus, setHomepageDataStatus] = useState('loading');
    const [homepageDataNotice, setHomepageDataNotice] = useState('');
    const [planMode, setPlanMode] = useState('seven');
    const [customPlanLength, setCustomPlanLength] = useState(3);
    const planStrategy = hasWeeklyBudget ? 'cheap' : 'value';
    const [hasUserAdjustedPlan, setHasUserAdjustedPlan] = useState(false);
    const [planRecipeIds, setPlanRecipeIds] = useState([]);
    const [replaceSlotIndex, setReplaceSlotIndex] = useState(null);
    const [replacementFeedback, setReplacementFeedback] = useState(null);
    const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
    const [isPlanPriceBreakdownOpen, setIsPlanPriceBreakdownOpen] = useState(false);
    const [isInventoryModeDetailsOpen, setIsInventoryModeDetailsOpen] = useState(false);
    const [checkedShoppingItems, setCheckedShoppingItems] = useState({});
    const [shoppingListShareStatus, setShoppingListShareStatus] = useState('');
    const [shoppingListPantryStatus, setShoppingListPantryStatus] = useState('');
    const [inventoryMode, setInventoryMode] = useState(savedProfile.inventoryMode);
    const [pantryState, setPantryState] = useState(() => loadPantryState());
    const [activeHeroRecipeIndex, setActiveHeroRecipeIndex] = useState(0);
    const locationSearchRunRef = useRef(0);
    const nearbyStoreLookupRunRef = useRef(0);
    const responsiveResultsRef = useRef(null);
    const desktopResultsRef = useRef(null);
    const alternativePanelRefs = useRef({});
    const replacementFeedbackTimerRef = useRef(null);

    useEffect(() => {
        const syncPantryState = () => setPantryState(loadPantryState());
        window.addEventListener('focus', syncPantryState);
        window.addEventListener('storage', syncPantryState);

        return () => {
            window.removeEventListener('focus', syncPantryState);
            window.removeEventListener('storage', syncPantryState);
        };
    }, []);

    const currentLocationLookupKey = useMemo(
        () => getLocationLookupKey(searchCriteria),
        [searchCriteria]
    );
    const isAddressSearchReady = Boolean(isAddressSuggestionConfirmed && currentLocationLookupKey)
        && (!searchSnapshot || getLocationLookupKey(searchSnapshot) !== currentLocationLookupKey);
    const hasCurrentNearbyStoreLookup = Boolean(currentLocationLookupKey)
        && nearbyStoreLookup.criteriaKey === currentLocationLookupKey
        && nearbyStoreLookup.status !== 'idle';
    const activeNearbyStoreKeys = useMemo(
        () => hasCurrentNearbyStoreLookup && nearbyStoreLookup.status === 'success'
            ? uniqueStoreKeys(nearbyStoreLookup.storeKeys)
            : [],
        [hasCurrentNearbyStoreLookup, nearbyStoreLookup.status, nearbyStoreLookup.storeKeys]
    );
    const shouldRestrictStoreButtons = nearbyStoreLookup.status === 'success'
        && activeNearbyStoreKeys.length > 0;
    const isStoreEnabledByLocation = (storeKey) => (
        !shouldRestrictStoreButtons || activeNearbyStoreKeys.includes(storeKey)
    );
    const selectedStoreKeys = useMemo(
        () => Object.keys(stores).filter((storeKey) => stores[storeKey].selected),
        [stores]
    );
    const selectedNearbyStoreKeys = useMemo(
        () => activeNearbyStoreKeys.filter((storeKey) => selectedStoreKeys.includes(storeKey)),
        [activeNearbyStoreKeys, selectedStoreKeys]
    );
    useEffect(() => {
        if (currentLocationLookupKey || selectedStoreKeys.length > 0) {
            setIsSearchCriteriaPromptVisible(false);
        }
    }, [currentLocationLookupKey, selectedStoreKeys.length]);
    useEffect(() => {
        if (!searchSnapshot || searchSnapshot.locationStatus === 'loading') {
            return;
        }

        const locationStoreKeySet = new Set(uniqueStoreKeys(searchSnapshot.locationStoreKeys));
        const nextSelectedStoreKeys = snapshotHasGeoLocation(searchSnapshot)
            ? selectedStoreKeys.filter((storeKey) => locationStoreKeySet.has(storeKey))
            : uniqueStoreKeys(selectedStoreKeys);
        const currentSelectedStoreKeys = uniqueStoreKeys(searchSnapshot.selectedStores);
        const selectionIsUnchanged = nextSelectedStoreKeys.length === currentSelectedStoreKeys.length
            && nextSelectedStoreKeys.every((storeKey) => currentSelectedStoreKeys.includes(storeKey));

        if (!selectionIsUnchanged) {
            setSearchSnapshot((currentSnapshot) => currentSnapshot
                ? { ...currentSnapshot, selectedStores: nextSelectedStoreKeys }
                : currentSnapshot);
        }
    }, [searchSnapshot, selectedStoreKeys]);
    const mobileStoreSelectionLabel = selectedStoreKeys.length === 0
        ? 'Ingen valgt'
        : selectedStoreKeys.length === 1
            ? '1 butik valgt'
            : `${selectedStoreKeys.length} butikker valgt`;
    const nearbyStorePreview = useMemo(() => {
        if (!hasCurrentNearbyStoreLookup) {
            return null;
        }

        const radiusLabel = `${Number(searchCriteria.radius || 2).toFixed(1).replace('.', ',')} km`;

        if (nearbyStoreLookup.status === 'loading') {
            return {
                status: 'loading',
                message: `Finder butikker inden for ${radiusLabel}…`,
            };
        }

        if (nearbyStoreLookup.status === 'success') {
            const storeCount = activeNearbyStoreKeys.length;
            const selectedCount = selectedNearbyStoreKeys.length;
            return {
                status: 'success',
                message: `${storeCount} ${storeCount === 1 ? 'butikstype fundet' : 'butikstyper fundet'} inden for ${radiusLabel}. ${selectedCount} valgt nedenfor.`,
            };
        }

        if (nearbyStoreLookup.status === 'empty') {
            return {
                status: 'empty',
                message: `Ingen af butikkerne blev fundet inden for ${radiusLabel}. Udvid radius eller vælg butikker manuelt.`,
            };
        }

        if (nearbyStoreLookup.status === 'error') {
            return {
                status: 'error',
                message: 'Butikker i nærheden kunne ikke hentes. Du kan stadig vælge butikker manuelt.',
            };
        }

        return null;
    }, [activeNearbyStoreKeys.length, hasCurrentNearbyStoreLookup, nearbyStoreLookup.status, searchCriteria.radius, selectedNearbyStoreKeys.length]);
    const recipesById = useMemo(
        () => new Map(recipeRecords.map((recipe) => [normalize(recipe.recipe_id), recipe])),
        [recipeRecords]
    );
    const mobileStoreKeys = useMemo(() => {
        const preferredStoreKeys = MOBILE_PRIMARY_STORE_TOKENS
            .map((token) => getStoreKeyFromName(token))
            .filter(Boolean);
        const uniquePreferredStoreKeys = Array.from(new Set(preferredStoreKeys))
            .filter((storeKey) => Stores[storeKey]);
        const remainingStoreKeys = Object.keys(Stores)
            .filter((storeKey) => !uniquePreferredStoreKeys.includes(storeKey));

        return [...uniquePreferredStoreKeys, ...remainingStoreKeys];
    }, []);

    const syncSelectedStoresToNearbyScope = (storeKeys) => {
        const scopedStoreKeys = uniqueStoreKeys(storeKeys);
        const scopedStoreKeySet = new Set(scopedStoreKeys);

        setStores((previousStores) => {
            let hasChanges = false;
            const nextStores = Object.keys(previousStores).reduce((next, storeKey) => {
                const shouldSelect = scopedStoreKeySet.has(storeKey);
                const previousStore = previousStores[storeKey];

                if (previousStore.selected !== shouldSelect) {
                    hasChanges = true;
                }

                next[storeKey] = {
                    ...previousStore,
                    selected: shouldSelect,
                };

                return next;
            }, {});

            return hasChanges ? nextStores : previousStores;
        });
    };

    const invalidateNearbyStoreLookup = (criteria) => {
        const criteriaKey = getLocationLookupKey(criteria);

        if (!criteriaKey) {
            return;
        }

        nearbyStoreLookupRunRef.current += 1;
        setNearbyStoreLookup({
            criteriaKey,
            status: 'loading',
            storeKeys: [],
        });
        syncSelectedStoresToNearbyScope([]);
    };

    const runNearbyStoreLookup = async (criteria) => {
        const criteriaKey = getLocationLookupKey(criteria);
        const selectedStoreKeysBeforeLookup = uniqueStoreKeys(selectedStoreKeys);

        if (!criteriaKey) {
            return [];
        }

        const runId = nearbyStoreLookupRunRef.current + 1;
        nearbyStoreLookupRunRef.current = runId;
        setNearbyStoreLookup({
            criteriaKey,
            status: 'loading',
            storeKeys: [],
        });
        syncSelectedStoresToNearbyScope([]);

        try {
            const storeKeys = await fetchNearbyStoreKeys(criteria);

            if (nearbyStoreLookupRunRef.current !== runId) {
                return storeKeys;
            }

            const resolvedStoreKeys = uniqueStoreKeys(storeKeys);
            const preferredResolvedStoreKeys = !savedProfile.allowAllNearbyStores
                && selectedStoreKeysBeforeLookup.length > 0
                ? resolvedStoreKeys.filter((storeKey) => selectedStoreKeysBeforeLookup.includes(storeKey))
                : resolvedStoreKeys;
            const status = resolvedStoreKeys.length > 0 ? 'success' : 'empty';
            setNearbyStoreLookup({
                criteriaKey,
                status,
                storeKeys: resolvedStoreKeys,
            });
            syncSelectedStoresToNearbyScope(preferredResolvedStoreKeys);

            setSearchSnapshot((currentSnapshot) => {
                if (!currentSnapshot || getLocationLookupKey(currentSnapshot) !== criteriaKey) {
                    return currentSnapshot;
                }

                return {
                    ...currentSnapshot,
                    locationStoreKeys: resolvedStoreKeys,
                    selectedStores: preferredResolvedStoreKeys,
                    locationStatus: status,
                };
            });

            return resolvedStoreKeys;
        } catch (error) {
            if (nearbyStoreLookupRunRef.current === runId) {
                setNearbyStoreLookup({
                    criteriaKey,
                    status: 'error',
                    storeKeys: [],
                });
            }

            return [];
        }
    };

    const toggleStoreSelection = (storeKey) => {
        if (!isStoreEnabledByLocation(storeKey)) {
            return;
        }

        setStores((previousStores) => {
            if (!previousStores[storeKey]) {
                return previousStores;
            }

            return {
                ...previousStores,
                [storeKey]: {
                    ...previousStores[storeKey],
                    selected: !previousStores[storeKey].selected,
                },
            };
        });
    };

    const renderMobileStoreButton = (storeKey, className = '') => {
        const store = Stores[storeKey];

        if (!store) {
            return null;
        }

        const isSelected = Boolean(stores[storeKey]?.selected);
        const isDisabled = !isStoreEnabledByLocation(storeKey);
        const isNearbyStore = activeNearbyStoreKeys.includes(storeKey);
        const isLocationMatch = isNearbyStore && isSelected;
        const storeLabel = MOBILE_STORE_LABELS[storeKey] || getStoreDisplayName(storeKey);
        const storeSlug = getStoreLookupSlug(storeKey) || normalizeStoreLookup(storeLabel);

        return (
            <button
                key={`mobile-store-${storeKey}`}
                type="button"
                className={`home-mobile-store-button${isSelected ? ' is-selected' : ''}${isLocationMatch ? ' is-location-match' : ''}${isDisabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
                data-store={storeSlug}
                onClick={() => toggleStoreSelection(storeKey)}
                disabled={isDisabled}
                aria-pressed={isSelected}
                aria-label={`${isNearbyStore ? 'Fundet i radius: ' : isDisabled ? 'Ikke fundet i radius: ' : ''}${storeLabel}. ${isSelected ? 'Valgt' : 'Ikke valgt'}`}
            >
                <span className="home-mobile-store-logo" aria-hidden="true">
                    <img src={store.logo} alt="" />
                </span>
                <span className="home-mobile-store-name">{storeLabel}</span>
                {isSelected && <CheckLg className="home-mobile-store-check" size={13} aria-hidden="true" />}
            </button>
        );
    };

    useEffect(() => {
        const loadHomepageData = async () => {
            setHomepageDataStatus('loading');
            setHomepageDataNotice('');

            const recommendationsPromise = fetchJsonOrThrow(api_url + 'api/recommendations/weekly').catch(() => null);
            const recipesJson = await fetchJsonOrThrow(api_url + 'api/recipes');
            if (!Array.isArray(recipesJson)) {
                throw new Error('Invalid recipe payload');
            }

            const recommendationsJson = await recommendationsPromise;
            const dataNotice = recommendationsJson
                ? ''
                : 'Ugens tilbudsdata kunne ikke hentes, så vi viser opskrifter uden tilbudspriser.';

            setRecipeRecords(recipesJson);
            setWeeklyRecommendations(recommendationsJson && typeof recommendationsJson === 'object' ? recommendationsJson : null);
            setHomepageDataNotice(dataNotice);
            setHomepageDataStatus('ready');
        };

        loadHomepageData().catch(() => {
            setRecipeRecords([]);
            setWeeklyRecommendations(null);
            setHomepageDataNotice('');
            setHomepageDataStatus('error');
        });
    }, []);

    const buildRecommendationCard = useMemo(() => {
        return (recommendation) => {
            const recipe = recipesById.get(normalize(recommendation.recipe_id)) || {};
            const imageCandidates = getRecipeImageCandidates(recipe, recommendation.titel);
            const weeklyPriceAmount = getWeeklyPriceAmount(recommendation);
            const weeklySavingsAmount = getWeeklySavingsAmount(recommendation) || 0;
            const coreIngredientNames = getCoreIngredientNames(recommendation).length > 0
                ? getCoreIngredientNames(recommendation)
                : getCoreIngredientNames(recipe);
            const matchedStoreNames = getOfferStoreNames(recommendation);
            const offerNames = Array.from(
                new Set(
                    (recommendation.matched_offers || [])
                        .filter((offer) => normalize(offer.match_source) === 'name')
                        .map((offer) => formatIngredientLabel(offer.produkt_navn))
                        .filter(Boolean)
                )
            ).slice(0, 2);
            return {
                ...recommendation,
                ingredients: Array.isArray(recipe.ingredients) ? recipe.ingredients : recommendation.ingredients,
                imageCandidates,
                image: imageCandidates[0] || PLACEHOLDER_IMAGE,
                title: recipe.name || recommendation.titel,
                timeLabel: formatTimeRange(recipe.tid_min || recommendation.aktiv_tid_min, recipe.tid_max || recommendation.total_tid_min),
                portionsLabel: normalize(recipe.portions || recommendation.portioner),
                reason: buildRecommendationReason(recommendation),
                recipeType: normalize(recommendation.ret_type || recipe.ret_type),
                primaryProtein: normalize(recommendation.primaer_protein || recipe.primaer_protein || coreIngredientNames[0]),
                diversityGroup: normalize(recommendation.diversitet_gruppe || recipe.diversitet_gruppe),
                recommendationScore: Number(recommendation.samlet_score_foreloebig_0_100 || 0),
                weeklyPriceAmount,
                weeklySavingsAmount,
                weeklyPriceLabel: buildWeeklyShoppingPrice(recommendation),
                weeklySavingsLabel: buildWeeklySavingsLabel(recommendation),
                weeklyOfferSummary: offerNames.length > 0 ? `På tilbud nu: ${offerNames.join(', ')}` : '',
                nutritionHighlight: buildNutritionHighlight(recommendation),
                matchedStoreNames,
                coreIngredientNames,
                isWeeklyRecommendation: true,
            };
        };
    }, [recipesById]);

    const buildCatalogRecipeCard = useMemo(() => {
        return (recipe) => {
            const imageCandidates = getRecipeImageCandidates(recipe, recipe.name);
            const weeklyPriceAmount = getWeeklyPriceAmount(recipe);
            const weeklySavingsAmount = getWeeklySavingsAmount(recipe) || 0;
            const coreIngredientNames = getCoreIngredientNames(recipe);
            const matchedStoreNames = getOfferStoreNames(recipe);
            const nutritionHighlight = buildNutritionHighlight(recipe);

            return {
                ...recipe,
                imageCandidates,
                image: imageCandidates[0] || PLACEHOLDER_IMAGE,
                title: recipe.name,
                timeLabel: formatTimeRange(recipe.tid_min, recipe.tid_max),
                portionsLabel: normalize(recipe.portions),
                reason: coreIngredientNames.length > 0
                    ? `Matcher planen med ${coreIngredientNames.slice(0, 2).join(' og ').toLowerCase()}`
                    : nutritionHighlight || 'God hverdagsret til planen',
                recipeType: normalize(recipe.ret_type || recipe.maaltidsprofil),
                primaryProtein: normalize(recipe.primaer_protein || coreIngredientNames[0]),
                diversityGroup: normalize(recipe.diversitet_gruppe || coreIngredientNames.slice(0, 2).join('|') || recipe.recipe_id),
                recommendationScore: Number(recipe.samlet_score_foreloebig_0_100 || 0),
                weeklyPriceAmount,
                weeklySavingsAmount,
                weeklyPriceLabel: weeklyPriceAmount === null ? '' : `Køb ind for ${formatDkkAmount(weeklyPriceAmount)}`,
                weeklySavingsLabel: weeklySavingsAmount > 0 ? `Spar ca. ${formatDkkAmount(weeklySavingsAmount)} i denne uge` : '',
                weeklyOfferSummary: matchedStoreNames.length > 0 ? `Pris fra ${matchedStoreNames.slice(0, 2).join(', ')}` : '',
                nutritionHighlight,
                matchedStoreNames,
                coreIngredientNames,
                isCatalogFallback: true,
            };
        };
    }, []);

    const recommendationCards = useMemo(() => {
        if (!searchSnapshot || !weeklyRecommendations) {
            return [];
        }

        const scopedStoreKeys = getSnapshotStoreScopeKeys(searchSnapshot);
        const enforceScopedStores = shouldEnforceStoreScope(searchSnapshot);

        if (enforceScopedStores && scopedStoreKeys.length === 0) {
            return [];
        }

        const selectedRecipes = selectHomepageRecommendations(
            weeklyRecommendations,
            getSelectedStoreNames(scopedStoreKeys),
            enforceScopedStores ? 48 : 10
        );

        const weeklyCards = selectedRecipes
            .map((recommendation) => applyStoreScopeToRecipePricing(recommendation, scopedStoreKeys))
            .filter((recommendation) => !enforceScopedStores || recipeHasScopedDiscountSource(recommendation, scopedStoreKeys))
            .map(buildRecommendationCard)
            .filter((card) => card.recipe_id);
        const weeklyCardIds = new Set(weeklyCards.map((card) => normalize(card.recipe_id)));
        const catalogCards = recipeRecords
            .filter((recipe) => recipe?.recipe_id && !weeklyCardIds.has(normalize(recipe.recipe_id)))
            .map((recipe) => applyStoreScopeToRecipePricing(recipe, scopedStoreKeys))
            .filter((recipe) => !enforceScopedStores || recipeHasScopedDiscountSource(recipe, scopedStoreKeys))
            .map(buildCatalogRecipeCard)
            .filter((card) => card.recipe_id && card.weeklyPriceAmount !== null)
            .sort((left, right) =>
                (right.weeklySavingsAmount || 0) - (left.weeklySavingsAmount || 0)
                || (left.weeklyPriceAmount ?? 999) - (right.weeklyPriceAmount ?? 999)
                || normalize(left.title).localeCompare(normalize(right.title), 'da-DK')
            );

        return [...weeklyCards, ...catalogCards];
    }, [buildCatalogRecipeCard, buildRecommendationCard, recipeRecords, searchSnapshot, weeklyRecommendations]);

    const heroRecipeCards = useMemo(() => {
        const scopedStoreKeys = searchSnapshot
            ? getSnapshotStoreScopeKeys(searchSnapshot)
            : uniqueStoreKeys(selectedStoreKeys);
        const enforceScopedStores = searchSnapshot
            ? shouldEnforceStoreScope(searchSnapshot)
            : scopedStoreKeys.length > 0;

        if (enforceScopedStores && scopedStoreKeys.length === 0) {
            return [];
        }

        if (weeklyRecommendations) {
            const selectedRecipes = selectHomepageRecommendations(
                weeklyRecommendations,
                getSelectedStoreNames(scopedStoreKeys),
                enforceScopedStores ? 48 : MAX_PLAN_LENGTH
            );

            return selectedRecipes
                .map((recommendation) => applyStoreScopeToRecipePricing(recommendation, scopedStoreKeys))
                .filter((recommendation) => !enforceScopedStores || recipeHasScopedDiscountSource(recommendation, scopedStoreKeys))
                .map(buildRecommendationCard)
                .filter((recipe) => recipe.recipe_id)
                .slice(0, MAX_PLAN_LENGTH);
        }

        return recipeRecords
            .map((recipe) => applyStoreScopeToRecipePricing(recipe, scopedStoreKeys))
            .filter((recipe) => !enforceScopedStores || recipeHasScopedDiscountSource(recipe, scopedStoreKeys))
            .slice(0, MAX_PLAN_LENGTH)
            .map(buildCatalogRecipeCard)
            .filter((recipe) => recipe.recipe_id);
    }, [buildCatalogRecipeCard, buildRecommendationCard, recipeRecords, searchSnapshot, selectedStoreKeys, weeklyRecommendations]);

    const heroPlanCards = useMemo(
        () => heroRecipeCards.slice(0, MAX_PLAN_LENGTH),
        [heroRecipeCards]
    );

    const fallbackHeroStoreNames = useMemo(
        () => getSelectedStoreNames(searchSnapshot ? getSnapshotStoreScopeKeys(searchSnapshot) : selectedStoreKeys),
        [searchSnapshot, selectedStoreKeys]
    );

    const heroCollectedPlanCards = useMemo(
        () => heroPlanCards.slice(0, Math.min(activeHeroRecipeIndex, MAX_PLAN_LENGTH)),
        [activeHeroRecipeIndex, heroPlanCards]
    );

    const heroCollectedPlanTotals = useMemo(() => {
        const totalPrice = heroCollectedPlanCards.reduce((sum, card) => sum + (card.weeklyPriceAmount || 0), 0);
        const totalSavings = heroCollectedPlanCards.reduce((sum, card) => sum + (card.weeklySavingsAmount || 0), 0);
        const storeNames = Array.from(new Set(heroCollectedPlanCards
            .flatMap((card) => (Array.isArray(card.matchedStoreNames) ? card.matchedStoreNames : []))
            .map(normalize)
            .filter(Boolean)));

        return {
            totalPrice,
            totalSavings,
            storeNames,
        };
    }, [heroCollectedPlanCards]);

    useEffect(() => {
        if (heroRecipeCards.length <= 1) {
            setActiveHeroRecipeIndex(0);
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setActiveHeroRecipeIndex((currentIndex) =>
                currentIndex >= heroRecipeCards.length - 1 ? 0 : currentIndex + 1
            );
        }, 3000);

        return () => window.clearInterval(intervalId);
    }, [heroRecipeCards.length]);

    useEffect(() => {
        if (
            (heroRecipeCards.length === 0 && activeHeroRecipeIndex !== 0)
            || activeHeroRecipeIndex > heroRecipeCards.length
        ) {
            setActiveHeroRecipeIndex(0);
        }
    }, [activeHeroRecipeIndex, heroRecipeCards.length]);

    const activeHeroRecipeDisplayIndex = heroRecipeCards.length > 0
        ? Math.min(activeHeroRecipeIndex, heroRecipeCards.length - 1)
        : 0;
    const activeHeroRecipe = heroRecipeCards[activeHeroRecipeDisplayIndex] || null;
    const activeHeroStoreMeta = activeHeroRecipe
        ? getHeroRecipeStoreMeta(activeHeroRecipe, fallbackHeroStoreNames)
        : { name: '', logo: null };
    const activeHeroSavingsLabel = activeHeroRecipe ? formatDishSavingsLabel(activeHeroRecipe) : '';
    const activeHeroSavingsAmountLabel = toFiniteNumber(activeHeroRecipe?.weeklySavingsAmount) > 0
        ? formatDkkAmount(activeHeroRecipe.weeklySavingsAmount)
        : activeHeroSavingsLabel.replace(/^Spar\s+/i, '');
    const activeHeroPriceLabel = activeHeroRecipe ? formatPlannerPriceLabel(activeHeroRecipe) : '';
    // Keep the phone hero aligned with the rotating weekly suggestion below it.
    // The visual only exposes the deal, while the recipe remains available through the link.
    const mobileHeroPreviewRecipe = activeHeroRecipe;
    const mobileHeroPreviewPriceLabel = mobileHeroPreviewRecipe
        ? formatPlannerPriceLabel(mobileHeroPreviewRecipe)
        : '';
    const mobileHeroPreviewSavingsLabel = mobileHeroPreviewRecipe
        ? formatDishSavingsLabel(mobileHeroPreviewRecipe)
        : '';
    const heroCollectedStoreSummary = heroCollectedPlanTotals.storeNames
        .slice(0, 2)
        .join(', ');
    const handleRecipeImageError = (event) => {
        const imageElement = event.currentTarget;
        const candidates = JSON.parse(imageElement.dataset.imageCandidates || '[]');
        const currentIndex = Number(imageElement.dataset.imageIndex || '0');
        const nextIndex = currentIndex + 1;

        if (nextIndex < candidates.length) {
            imageElement.dataset.imageIndex = `${nextIndex}`;
            imageElement.src = candidates[nextIndex];
            return;
        }

        imageElement.onerror = null;
        imageElement.src = PLACEHOLDER_IMAGE;
    };

    const cappedPlanLength = planMode === 'seven'
        ? MAX_PLAN_LENGTH
        : Math.max(1, Math.min(customPlanLength, MAX_PLAN_LENGTH));
    const rankedPlannerCards = useMemo(
        () => rankPlannerCards(recommendationCards, planStrategy),
        [planStrategy, recommendationCards]
    );
    const plannerCandidateCards = useMemo(
        () => rankedPlannerCards,
        [rankedPlannerCards]
    );
    const recommendationCardsById = useMemo(
        () => new Map(recommendationCards.map((card) => [card.recipe_id, card])),
        [recommendationCards]
    );

    useEffect(() => {
        const orderedIds = selectPlannerCardIds(
            plannerCandidateCards,
            cappedPlanLength,
            weeklyBudget
        );

        if (orderedIds.length === 0) {
            setPlanRecipeIds([]);
            setReplaceSlotIndex(null);
            return;
        }

        setPlanRecipeIds((previousIds) => {
            const validPrevious = previousIds.filter(
                (recipeId, index, array) =>
                    orderedIds.includes(recipeId) && array.indexOf(recipeId) === index
            );

            const nextIds = [...validPrevious];
            for (const recipeId of orderedIds) {
                if (nextIds.length >= cappedPlanLength) {
                    break;
                }
                if (!nextIds.includes(recipeId)) {
                    nextIds.push(recipeId);
                }
            }
            return nextIds.slice(0, cappedPlanLength);
        });
    }, [plannerCandidateCards, cappedPlanLength, weeklyBudget]);

    const currentPlanCards = useMemo(
        () => planRecipeIds
            .map((recipeId) => recommendationCardsById.get(recipeId))
            .filter(Boolean),
        [planRecipeIds, recommendationCardsById]
    );
    const visiblePlanCards = useMemo(
        () => currentPlanCards,
        [currentPlanCards]
    );

    const alternativeCards = useMemo(() => {
        if (replaceSlotIndex === null) {
            return [];
        }

        const currentCard = visiblePlanCards[replaceSlotIndex];
        const currentKey = currentCard ? getPlannerCardKey(currentCard) : '';
        const currentProtein = normalize(currentCard?.primaryProtein).toLowerCase();

        const currentPlanPrice = visiblePlanCards.reduce(
            (total, card) => total + (toFiniteNumber(card.weeklyPriceAmount) || 0),
            0
        );
        const currentCardPrice = toFiniteNumber(currentCard?.weeklyPriceAmount) || 0;

        return plannerCandidateCards
            .filter((card) => card.recipe_id && !planRecipeIds.includes(card.recipe_id))
            .filter((card) => {
                if (!hasWeeklyBudget) {
                    return true;
                }

                const candidatePrice = toFiniteNumber(card.weeklyPriceAmount);
                return candidatePrice !== null
                    && currentPlanPrice - currentCardPrice + candidatePrice <= weeklyBudget;
            })
            .map((card) => {
                const cardKey = getPlannerCardKey(card);
                const cardProtein = normalize(card.primaryProtein).toLowerCase();
                const differenceBonus =
                    (cardKey && cardKey !== currentKey ? 16 : 0) +
                    (cardProtein && cardProtein !== currentProtein ? 12 : 0) +
                    (card.isWeeklyRecommendation ? 10 : 0);

                return {
                    ...card,
                    alternativeScore: scorePlannerCard(card, planStrategy) + differenceBonus,
                    replacementReasons: buildReplacementRationale(currentCard, card, planStrategy),
                };
            })
            .sort((left, right) =>
                right.alternativeScore - left.alternativeScore
                || (left.weeklyPriceAmount ?? 999) - (right.weeklyPriceAmount ?? 999)
                || normalize(left.title).localeCompare(normalize(right.title), 'da-DK')
            )
            .slice(0, 8);
    }, [hasWeeklyBudget, planRecipeIds, planStrategy, plannerCandidateCards, replaceSlotIndex, visiblePlanCards, weeklyBudget]);
    const showCustomAddCard = planMode === 'custom'
        && customPlanLength < MAX_PLAN_LENGTH
        && visiblePlanCards.length > 0
        && visiblePlanCards.length < plannerCandidateCards.length;
    const visibleResultItemCount = visiblePlanCards.length + (showCustomAddCard ? 1 : 0);
    const isSparseResultPanel = visibleResultItemCount > 0 && visibleResultItemCount <= 2;
    const visibleMealCount = visiblePlanCards.length;
    const visibleMealLabel = visibleMealCount === 1 ? '1 ret' : `${visibleMealCount} retter`;
    const planSummaryLabel = planMode === 'seven' && visibleMealCount === MAX_PLAN_LENGTH
        ? 'Hele ugen'
        : visibleMealLabel;
    const planTotals = useMemo(() => {
        const totalPrice = visiblePlanCards.reduce((sum, card) => sum + (card.weeklyPriceAmount || 0), 0);
        const totalSavings = visiblePlanCards.reduce((sum, card) => sum + (card.weeklySavingsAmount || 0), 0);
        const averagePrice = visiblePlanCards.length > 0 ? totalPrice / visiblePlanCards.length : 0;

        return {
            totalPrice,
            totalSavings,
            averagePrice,
        };
    }, [visiblePlanCards]);
    const pantryItems = useMemo(() => pantryStateToItems(pantryState), [pantryState]);
    const fullShoppingListGroups = useMemo(
        () => buildPlanShoppingGroups(visiblePlanCards),
        [visiblePlanCards]
    );
    const shoppingListGroups = useMemo(() => applyPantryToShoppingGroups(
        fullShoppingListGroups,
        pantryItems
    ), [fullShoppingListGroups, pantryItems]);
    const purchasableShoppingItems = useMemo(
        () => shoppingListGroups.flatMap((group) =>
            group.items.filter((item) => !item.isFullyCoveredByPantry)
        ),
        [shoppingListGroups]
    );
    const shoppingListItemCount = purchasableShoppingItems.length;
    const visibleShoppingListGroups = useMemo(
        () => getPurchasableShoppingGroups(shoppingListGroups),
        [shoppingListGroups]
    );
    const pantryCoveredShoppingItemCount = shoppingListGroups.reduce(
        (sum, group) => sum + group.items.filter((item) => item.isFullyCoveredByPantry).length,
        0
    );
    const shoppingListOfferStoreCount = shoppingListGroups.filter(
        (group) => group.isOfferStore && group.items.some((item) => !item.isFullyCoveredByPantry)
    ).length;
    const fullShoppingSummary = useMemo(
        () => summarizeShoppingGroups(fullShoppingListGroups, visibleMealCount),
        [fullShoppingListGroups, visibleMealCount]
    );
    const shoppingSummary = useMemo(
        () => summarizeShoppingGroups(shoppingListGroups, visibleMealCount),
        [shoppingListGroups, visibleMealCount]
    );
    const hasShoppingListPricing = fullShoppingSummary.weeklyTotal > 0;
    const displayedShoppingTotal = hasShoppingListPricing
        ? shoppingSummary.weeklyTotal
        : planTotals.totalPrice;
    const displayedShoppingSavings = fullShoppingSummary.normalTotal > 0
        ? shoppingSummary.savingsTotal
        : planTotals.totalSavings;
    const pantryCoveredValue = Math.max(
        0,
        fullShoppingSummary.weeklyTotal - shoppingSummary.weeklyTotal
    );
    const hasPantryPriceAdjustment = pantryCoveredValue >= 0.5;
    const checkedShoppingItemCount = shoppingListGroups.reduce(
        (sum, group) => sum + group.items.filter(
            (item) => !item.isFullyCoveredByPantry && checkedShoppingItems[item.key]
        ).length,
        0
    );
    const areAllShoppingItemsChecked = shoppingListItemCount > 0
        && checkedShoppingItemCount === shoppingListItemCount;

    useEffect(() => {
        const validKeys = new Set(
            purchasableShoppingItems.map((item) => item.key)
        );

        setCheckedShoppingItems((previousItems) => Object.fromEntries(
            Object.entries(previousItems).filter(([key, isChecked]) => isChecked && validKeys.has(key))
        ));
    }, [purchasableShoppingItems]);

    const handleShareShoppingList = async () => {
        const shareText = buildShoppingListShareText(shoppingListGroups, {
            mealCount: visibleMealCount,
            totalSavings: displayedShoppingSavings,
        });

        setShoppingListShareStatus('');

        try {
            if (navigator.share) {
                await navigator.share({
                    title: 'Indkøbsliste fra Budgetvenlig',
                    text: shareText,
                });
                setShoppingListShareStatus('Indkøbslisten er delt.');
                return;
            }

            const copied = await copyTextToClipboard(shareText);
            setShoppingListShareStatus(copied
                ? 'Indkøbslisten er kopieret og klar til at sende.'
                : 'Indkøbslisten kunne ikke kopieres automatisk.');
        } catch (error) {
            if (error?.name === 'AbortError') {
                return;
            }

            try {
                const copied = await copyTextToClipboard(shareText);
                setShoppingListShareStatus(copied
                    ? 'Indkøbslisten er kopieret og klar til at sende.'
                    : 'Indkøbslisten kunne ikke deles lige nu.');
            } catch {
                setShoppingListShareStatus('Indkøbslisten kunne ikke deles lige nu.');
            }
        }
    };

    const addShoppingItemsToPantry = (items, mode = 'precise') => {
        const itemKeys = new Set(items.map((item) => item.key));
        let nextPantry = loadPantryState();
        const addedKeys = new Set();

        shoppingListGroups.forEach((group) => {
            group.items.forEach((item) => {
                if (!itemKeys.has(item.key)) {
                    return;
                }

                const result = addShoppingItemToPantryState(nextPantry, item, {
                    note: mode === 'smart'
                        ? `Automatisk registreret fra madplan${group.isOfferStore ? ` hos ${group.name}` : ''}.`
                        : group.isOfferStore
                            ? `Købt via madplan hos ${group.name}.`
                            : 'Købt via madplan.',
                    trackingMode: mode,
                });
                if (!result.key) {
                    return;
                }

                nextPantry = result.state;
                addedKeys.add(result.key);
            });
        });

        if (addedKeys.size === 0) {
            return;
        }

        if (savePantryState(nextPantry)) {
            setPantryState(nextPantry);
            setShoppingListPantryStatus(
                mode === 'smart'
                    ? `Smart spisekammer har registreret ${addedKeys.size} ${addedKeys.size === 1 ? 'købt vare' : 'købte varer'}.`
                    : `${addedKeys.size} ${addedKeys.size === 1 ? 'vare er' : 'varer er'} lagt i dit spisekammer.`
            );
            setCheckedShoppingItems({});
            return;
        }

        setShoppingListPantryStatus('Varerne kunne ikke gemmes i spisekammeret på denne enhed.');
    };

    const handleAddCheckedItemsToPantry = () => {
        addShoppingItemsToPantry(
            purchasableShoppingItems.filter((item) => checkedShoppingItems[item.key]),
            'precise'
        );
    };

    const handleSmartPurchase = () => {
        addShoppingItemsToPantry(purchasableShoppingItems, 'smart');
    };

    const handleInventoryModeChange = (nextMode) => {
        setInventoryMode(nextMode);
        setIsInventoryModeDetailsOpen(false);
        setShoppingListPantryStatus('');
        saveProfile({
            ...loadProfile(),
            inventoryMode: nextMode,
        });
    };

    const setShoppingItemsChecked = (items, shouldCheck) => {
        setShoppingListPantryStatus('');
        setCheckedShoppingItems((previousItems) => {
            const nextItems = { ...previousItems };

            items.forEach((item) => {
                if (shouldCheck) {
                    nextItems[item.key] = true;
                } else {
                    delete nextItems[item.key];
                }
            });

            return nextItems;
        });
    };

    const handleToggleAllShoppingItems = () => {
        setShoppingItemsChecked(purchasableShoppingItems, !areAllShoppingItemsChecked);
    };
    const remainingWeeklyBudget = hasWeeklyBudget
        ? Math.max(0, weeklyBudget - displayedShoppingTotal)
        : null;
    const availableDiscountSources = useMemo(
        () => buildPlanDiscountSources(currentPlanCards),
        [currentPlanCards]
    );
    const planDiscountStoreKeys = useMemo(
        () => getStoreKeysFromDiscountSources(availableDiscountSources),
        [availableDiscountSources]
    );
    const availableDiscountTotal = availableDiscountSources.reduce((sum, source) => sum + (source.savingsAmount || 0), 0);
    const maxAvailableDiscountSavings = Math.max(
        1,
        ...availableDiscountSources.map((source) => source.savingsAmount || 0)
    );
    const heroMapStoreKeys = useMemo(() => {
        if (!searchSnapshot || searchSnapshot.locationStatus === 'loading') {
            return [];
        }

        const scopedStoreKeys = getSnapshotStoreScopeKeys(searchSnapshot);
        const locationStoreKeys = uniqueStoreKeys(searchSnapshot.locationStoreKeys);
        const storeKeysForMap = scopedStoreKeys.length > 0
            ? scopedStoreKeys
            : locationStoreKeys.length > 0
                ? locationStoreKeys
                : planDiscountStoreKeys;

        return storeKeysForMap.slice(0, 6);
    }, [planDiscountStoreKeys, searchSnapshot]);
    const heroMapStoreSummary = formatStoreKeyList(
        heroMapStoreKeys,
        3
    );

    const applyStoreSelection = (storeKeys, options = {}) => {
        const nextSelectedStoreKeys = uniqueStoreKeys(storeKeys);

        if (!options.replace && nextSelectedStoreKeys.length === 0) {
            return;
        }

        setStores((previousStores) => {
            const nextStores = { ...previousStores };
            const selectedKeySet = new Set(nextSelectedStoreKeys);

            if (options.replace) {
                Object.keys(nextStores).forEach((storeKey) => {
                    nextStores[storeKey] = {
                        ...nextStores[storeKey],
                        selected: selectedKeySet.has(storeKey),
                    };
                });

                return nextStores;
            }

            nextSelectedStoreKeys.forEach((storeKey) => {
                if (nextStores[storeKey]) {
                    nextStores[storeKey] = {
                        ...nextStores[storeKey],
                        selected: true,
                    };
                }
            });

            return nextStores;
        });
    };

    const shouldUseResponsiveResultsFlow = () =>
        typeof window !== 'undefined' && window.matchMedia('(max-width: 1199.98px)').matches;

    const completeLocationSequence = (storeKeys, finalStatus, runId, options = {}) => {
        const resolvedStoreKeys = uniqueStoreKeys(storeKeys);
        const hasLocationSearch = Boolean(searchCriteria.geoLocation?.lat && searchCriteria.geoLocation?.lon);
        const locationCriteriaKey = getLocationLookupKey(searchCriteria);
        const hasExplicitStoreSelection = Array.isArray(options.selectedStoreKeys);
        const explicitStoreKeySet = new Set(uniqueStoreKeys(options.selectedStoreKeys));
        const selectedStoresForSearch = hasLocationSearch
            ? hasExplicitStoreSelection
                ? resolvedStoreKeys.filter((storeKey) => explicitStoreKeySet.has(storeKey))
                : resolvedStoreKeys
            : selectedStoreKeys.length > 0
                ? uniqueStoreKeys(selectedStoreKeys)
                : resolvedStoreKeys;

        if (hasLocationSearch && locationCriteriaKey && locationSearchRunRef.current === runId) {
            setNearbyStoreLookup({
                criteriaKey: locationCriteriaKey,
                status: finalStatus,
                storeKeys: resolvedStoreKeys,
            });
        }

        applyStoreSelection(selectedStoresForSearch, { replace: hasLocationSearch });
        setSearchSnapshot((currentSnapshot) => {
            if (!currentSnapshot || locationSearchRunRef.current !== runId) {
                return currentSnapshot;
            }

            return {
                ...currentSnapshot,
                locationStoreKeys: resolvedStoreKeys,
                selectedStores: selectedStoresForSearch,
                locationStatus: finalStatus,
            };
        });
        scrollResultsIntoView(0);
        scrollResultsIntoView(90);
    };

    const scrollResultsIntoView = (delay = 80) => {
        if (typeof window === 'undefined') {
            return;
        }

        window.setTimeout(() => {
            const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
            const scrollTarget = isDesktop
                ? desktopResultsRef.current
                : responsiveResultsRef.current;

            if (!scrollTarget) {
                return;
            }

            const panelRect = scrollTarget.getBoundingClientRect();
            const viewportHeight = window.innerHeight || 1;
            const headerClearance = isDesktop ? 86 : 70;
            const comfortableTop = isDesktop
                ? Math.min(190, Math.max(132, viewportHeight * 0.2))
                : headerClearance + 10;
            const bottomSafeTop = viewportHeight - Math.min(panelRect.height, viewportHeight - headerClearance - 32) - 40;
            const desiredTop = Math.max(headerClearance + 10, Math.min(comfortableTop, bottomSafeTop));
            const targetTop = panelRect.top + window.scrollY - desiredTop;

            window.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth',
            });
        }, delay);
    };

    const openResponsiveResults = () => {
        const defaultPlan = getResponsivePlanDefaults();
        const hasLocation = Boolean(searchCriteria.geoLocation?.lat && searchCriteria.geoLocation?.lon);

        if (!hasLocation && selectedStoreKeys.length === 0) {
            locationSearchRunRef.current += 1;
            setSearchSnapshot(null);
            setIsSearchCriteriaPromptVisible(true);
            return;
        }

        setIsSearchCriteriaPromptVisible(false);
        const isResponsiveResultsFlow = shouldUseResponsiveResultsFlow();
        const runId = locationSearchRunRef.current + 1;
        locationSearchRunRef.current = runId;

        const manuallySelectedStoreKeys = uniqueStoreKeys(selectedStoreKeys);
        const hasResolvedNearbyLookup = hasLocation
            && hasCurrentNearbyStoreLookup
            && (nearbyStoreLookup.status === 'success' || nearbyStoreLookup.status === 'empty');
        const selectedNearbyStoreKeysForSearch = hasResolvedNearbyLookup
            ? activeNearbyStoreKeys.filter((storeKey) => manuallySelectedStoreKeys.includes(storeKey))
            : null;
        const savedStoreKeysForSearch = !savedProfile.allowAllNearbyStores
            && manuallySelectedStoreKeys.length > 0
            ? manuallySelectedStoreKeys
            : null;

        setPlanMode(defaultPlan.mode);
        setCustomPlanLength(defaultPlan.length);
        setHasUserAdjustedPlan(false);
        setReplacementFeedback(null);
        setSearchSnapshot({
            ...searchCriteria,
            selectedStores: hasLocation ? [] : uniqueStoreKeys(selectedStoreKeys),
            locationStoreKeys: [],
            locationStatus: 'loading',
        });

        if (isResponsiveResultsFlow && hasLocation) {
            scrollResultsIntoView(0);
            scrollResultsIntoView(110);
        }

        if (!hasLocation) {
            completeLocationSequence(manuallySelectedStoreKeys, 'success', runId);
            return;
        }

        if (hasResolvedNearbyLookup) {
            completeLocationSequence(
                activeNearbyStoreKeys,
                activeNearbyStoreKeys.length > 0 ? 'success' : 'empty',
                runId,
                { selectedStoreKeys: selectedNearbyStoreKeysForSearch }
            );
            return;
        }

        fetchNearbyStoreKeys(searchCriteria)
            .then((locationStoreKeys) => {
                completeLocationSequence(
                    locationStoreKeys,
                    locationStoreKeys.length > 0 ? 'success' : 'empty',
                    runId,
                    { selectedStoreKeys: savedStoreKeysForSearch }
                );
            })
            .catch(() => {
                completeLocationSequence([], 'error', runId);
            });
    };

    const handleReplaceSelection = (replacementRecipeId) => {
        if (replaceSlotIndex === null) {
            return;
        }

        const replacementSlotIndex = replaceSlotIndex;
        const removedRecipeId = planRecipeIds[replacementSlotIndex];

        if (!replacementRecipeId || !removedRecipeId) {
            return;
        }

        setPlanRecipeIds((previousIds) => {
            if (!previousIds[replacementSlotIndex]) {
                return previousIds;
            }

            const nextIds = [...previousIds];
            nextIds[replacementSlotIndex] = replacementRecipeId;
            return nextIds;
        });

        setReplaceSlotIndex(null);
        window.clearTimeout(replacementFeedbackTimerRef.current);
        setReplacementFeedback({
            slotIndex: replacementSlotIndex,
            recipeId: replacementRecipeId,
        });
        replacementFeedbackTimerRef.current = window.setTimeout(() => {
            setReplacementFeedback(null);
        }, 1450);
    };

    useEffect(() => {
        const heroElement = heroRef.current;

        if (!heroElement) {
            return undefined;
        }

        let frameId = null;

        const updateParallax = () => {
            frameId = null;

            const rect = heroElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight || 1;
            const progress = Math.max(-1, Math.min(1, rect.top / viewportHeight));
            const introHideProgress = Math.max(0, Math.min(1, (-rect.top) / (viewportHeight * 0.16)));

            heroElement.style.setProperty('--hero-parallax-y', `${progress * -28}px`);
            heroElement.style.setProperty('--hero-intro-opacity', `${1 - introHideProgress}`);
            heroElement.style.setProperty('--hero-intro-shift', `${introHideProgress * -24}px`);
        };

        const onScroll = () => {
            if (frameId !== null) {
                return;
            }

            frameId = window.requestAnimationFrame(updateParallax);
        };

        updateParallax();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }

            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    useEffect(() => {
        if (!searchSnapshot) {
            return;
        }

        if (searchSnapshot.locationStatus === 'loading') {
            return;
        }

        scrollResultsIntoView(80);
    }, [searchSnapshot]);

    useEffect(() => () => {
        window.clearTimeout(replacementFeedbackTimerRef.current);
    }, []);

    useEffect(() => {
        if (!searchSnapshot || hasUserAdjustedPlan) {
            return undefined;
        }

        const applyResponsiveDefaults = () => {
            const defaultPlan = getResponsivePlanDefaults();
            setPlanMode(defaultPlan.mode);
            setCustomPlanLength(defaultPlan.length);
        };

        applyResponsiveDefaults();
        window.addEventListener('resize', applyResponsiveDefaults);

        return () => window.removeEventListener('resize', applyResponsiveDefaults);
    }, [hasUserAdjustedPlan, searchSnapshot]);

    useEffect(() => {
        if (replaceSlotIndex === null || typeof window === 'undefined') {
            return undefined;
        }

        const timerId = window.setTimeout(() => {
            const visiblePanel = Object.values(alternativePanelRefs.current)
                .find((panel) => panel && panel.offsetParent !== null);

            if (!visiblePanel) {
                return;
            }

            const selectedCard = visiblePanel.previousElementSibling?.classList?.contains('home-search-result-card')
                ? visiblePanel.previousElementSibling
                : visiblePanel.closest('.home-search-results-grid')?.querySelector('.home-search-result-card.is-replacing');
            const resultsPanel = visiblePanel.closest('.home-inline-results');
            const shouldAnchorToDish = window.matchMedia('(max-width: 1199.98px)').matches;
            const scrollTarget = shouldAnchorToDish && selectedCard
                ? selectedCard
                : resultsPanel || visiblePanel;
            const targetOffset = shouldAnchorToDish
                ? Math.min(72, Math.max(48, window.innerHeight * 0.08))
                : 96;
            const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - targetOffset;
            window.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth',
            });
        }, 80);

        return () => window.clearTimeout(timerId);
    }, [replaceSlotIndex]);

    const setCustomPlanCount = (nextLength) => {
        setHasUserAdjustedPlan(true);
        setPlanMode('custom');
        setCustomPlanLength(Math.max(1, Math.min(MAX_PLAN_LENGTH, nextLength)));
        setReplaceSlotIndex(null);
        setReplacementFeedback(null);
    };

    const renderSuggestionPanel = (placement) => {
        if (!searchSnapshot) {
            return null;
        }

        const searchHasLocation = Boolean(searchSnapshot.geoLocation?.lat && searchSnapshot.geoLocation?.lon);
        const selectedSnapshotStores = Array.isArray(searchSnapshot.selectedStores)
            ? searchSnapshot.selectedStores
            : [];
        const locationStoreKeys = Array.isArray(searchSnapshot.locationStoreKeys)
            ? searchSnapshot.locationStoreKeys
            : [];
        const scopedSearchStoreKeys = getSnapshotStoreScopeKeys(searchSnapshot);
        const activeStoreKeys = scopedSearchStoreKeys.length > 0
            ? scopedSearchStoreKeys
            : locationStoreKeys.length > 0
                ? uniqueStoreKeys(locationStoreKeys)
                : planDiscountStoreKeys.length > 0
                    ? planDiscountStoreKeys
                    : uniqueStoreKeys(selectedSnapshotStores);
        const locationStoreSummary = activeStoreKeys.length > 0
            ? formatStoreKeyList(activeStoreKeys, 6)
            : searchSnapshot.locationStatus === 'loading'
                ? 'Finder butikker i radius...'
                : searchSnapshot.locationStatus === 'error'
                    ? 'Kunne ikke hente butikker i radius'
                    : searchSnapshot.locationStatus === 'empty'
                        ? 'Ingen kendte butikstyper fundet i radius'
                        : 'Vælg butikker eller brug din lokation';
        const isLocationLoading = searchSnapshot.locationStatus === 'loading';
        const renderAlternativePanel = (slotIndex, variant = 'inline') => {
            const currentReplacementCard = visiblePlanCards[slotIndex];

            return (
                <div
                    key={`alternatives-${placement}-${variant}-${slotIndex}`}
                    ref={(element) => {
                        alternativePanelRefs.current[`${placement}-${variant}`] = element;
                    }}
                    className={`home-plan-alternatives home-plan-alternatives-${variant}`}
                >
                <div className="home-plan-alternatives-header">
                    <div>
                        <p className="home-plan-alternatives-title">
                            Alternativer til Ret {slotIndex + 1}
                        </p>
                        <span className="home-plan-alternatives-subtitle">
                            {currentReplacementCard?.title
                                ? `Erstat ${currentReplacementCard.title}`
                                : 'Vælg en anden ret'}
                        </span>
                    </div>
                </div>
                {alternativeCards.length > 0 ? (
                    <div className="home-plan-alternatives-list">
                        {alternativeCards.map((recipe) => (
                            <button
                                key={recipe.recipe_id}
                                type="button"
                                className="home-plan-alternative-item"
                                onClick={() => handleReplaceSelection(recipe.recipe_id)}
                            >
                                <img
                                    src={recipe.image}
                                    alt={recipe.title}
                                    className="home-plan-alternative-image"
                                    data-image-index="0"
                                    data-image-candidates={JSON.stringify(recipe.imageCandidates || [])}
                                    style={{ objectPosition: getRecipeImagePosition(recipe.recipe_id) }}
                                    onError={handleRecipeImageError}
                                />
                                <span className="home-plan-alternative-copy">
                                    <span className="home-plan-alternative-name">{recipe.title}</span>
                                    <span className="home-plan-alternative-meta">
                                        {[formatPlannerPriceLabel(recipe), recipe.timeLabel].filter(Boolean).join(' · ')}
                                    </span>
                                    {recipe.replacementReasons?.length > 0 && (
                                        <span className="home-plan-alternative-reasons" aria-label="Hvorfor denne ret passer">
                                            {recipe.replacementReasons.map((reason) => (
                                                <span key={reason}>{reason}</span>
                                            ))}
                                        </span>
                                    )}
                                    <span className="home-plan-alternative-action">Vælg ret</span>
                                </span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="home-inline-results-empty">
                        Der er ikke flere alternativer i denne prototype endnu.
                    </p>
                )}
                </div>
            );
        };

        return (
            <div
                ref={placement === 'route' ? desktopResultsRef : responsiveResultsRef}
                className={`home-inline-results ${placement === 'route' ? 'home-route-results-panel' : 'home-hero-results-panel'} is-visible${isSparseResultPanel ? ' is-sparse' : ''}${isLocationLoading ? ' is-loading' : ''}`}
                aria-live="polite"
            >
                <div className="home-inline-results-header">
                    <div className="home-inline-results-copy">
                        <h4 className="home-inline-results-title">Planlæg hele ugen på tilbud</h4>
                        <p className="home-inline-results-intro">
                            {hasWeeklyBudget
                                ? `Her er de retter, der kan holdes inden for dit ugentlige loft på ${formatDkkAmount(weeklyBudget)}. Du kan skifte enkelte retter og justere antallet.`
                                : 'Her er en madplan med retter, der matcher ugens tilbud i butikkerne vist her. Du kan skifte enkelte retter og justere antallet.'}
                        </p>
                    </div>
                    <div className="home-inline-results-actions">
                        <div className="home-plan-length-filter" role="group" aria-label="Vælg madplanstype">
                            <button
                                type="button"
                                className={`home-plan-length-option${planMode === 'seven' ? ' is-active' : ''}`}
                                onClick={() => {
                                    setHasUserAdjustedPlan(true);
                                    setPlanMode('seven');
                                    setCustomPlanLength(MAX_PLAN_LENGTH);
                                    setReplaceSlotIndex(null);
                                }}
                            >
                                Hele ugen
                            </button>
                            <button
                                type="button"
                                className={`home-plan-length-option${planMode === 'custom' ? ' is-active' : ''}`}
                                onClick={() => {
                                    setCustomPlanCount(customPlanLength >= MAX_PLAN_LENGTH ? DEFAULT_CUSTOM_PLAN_LENGTH : customPlanLength);
                                }}
                            >
                                Vælg antal
                            </button>
                            <div className={`home-plan-custom-controls${planMode === 'custom' ? ' is-visible' : ''}`} aria-label="Tilpas antal retter">
                                <button
                                    type="button"
                                    className="home-plan-custom-stepper"
                                    onClick={() => setCustomPlanCount(customPlanLength - 1)}
                                    aria-label="Fjern en ret"
                                    disabled={customPlanLength <= 1}
                                >
                                    <DashLg size={12} aria-hidden="true" />
                                </button>
                                <span className="home-plan-custom-count">{customPlanLength}</span>
                                <button
                                    type="button"
                                    className="home-plan-custom-stepper"
                                    onClick={() => setCustomPlanCount(customPlanLength + 1)}
                                    aria-label="Tilføj en ret"
                                    disabled={customPlanLength >= MAX_PLAN_LENGTH}
                                >
                                    <PlusLg size={12} aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="home-inline-results-close"
                            onClick={() => {
                                setReplacementFeedback(null);
                                setSearchSnapshot(null);
                            }}
                            aria-label="Luk forslag"
                        >
                            Luk
                        </button>
                    </div>
                </div>
                {isLocationLoading && (
                    <div className="home-responsive-loading" role="status" aria-live="polite">
                        <span className="home-responsive-loading-spinner" aria-hidden="true" />
                        <span>
                            <strong>Finder butikker og retter</strong>
                            <em>Vi matcher tilbuddene med opskrifterne.</em>
                        </span>
                    </div>
                )}
                <div className="home-plan-overview" aria-label="Madplanens prisoversigt">
                    <div className="home-plan-value-focus">
                        <div className="home-plan-value-primary">
                            <span>Ugens indkøb</span>
                            <strong>
                                {hasShoppingListPricing || displayedShoppingTotal > 0
                                    ? formatDkkAmount(displayedShoppingTotal)
                                    : 'Beregnes'}
                            </strong>
                            <small>Efter spisekammer og tilbud</small>
                        </div>
                        <p className="home-plan-value-proof">
                            {hasPantryPriceAdjustment && displayedShoppingSavings > 0
                                ? <>Du undgår ca. <strong>{formatDkkAmount(pantryCoveredValue)}</strong> i genkøb fra dit spisekammer og sparer ca. <strong>{formatDkkAmount(displayedShoppingSavings)}</strong> på tilbud.</>
                                : hasPantryPriceAdjustment
                                    ? <>Du undgår ca. <strong>{formatDkkAmount(pantryCoveredValue)}</strong> i genkøb, fordi varerne allerede findes i dit spisekammer.</>
                                    : displayedShoppingSavings > 0
                                        ? <>Du sparer ca. <strong>{formatDkkAmount(displayedShoppingSavings)}</strong> på tilbud i den samlede indkøbsliste.</>
                                        : 'Beløbet er beregnet ud fra den samlede indkøbsliste.'}
                        </p>
                        <button
                            type="button"
                            className="home-plan-value-breakdown-toggle"
                            onClick={() => setIsPlanPriceBreakdownOpen((isOpen) => !isOpen)}
                            aria-expanded={isPlanPriceBreakdownOpen}
                            aria-controls={`home-plan-price-breakdown-${placement}`}
                        >
                            <span>{isPlanPriceBreakdownOpen ? 'Skjul beregning' : 'Se beregning'}</span>
                            <ArrowRight size={14} aria-hidden="true" />
                        </button>
                    </div>
                    {isPlanPriceBreakdownOpen && (
                        <div
                            id={`home-plan-price-breakdown-${placement}`}
                            className="home-plan-value-breakdown"
                        >
                            <div>
                                <span>Indkøb uden spisekammer</span>
                                <strong>{formatDkkAmount(fullShoppingSummary.weeklyTotal)}</strong>
                            </div>
                            {hasPantryPriceAdjustment && (
                                <div>
                                    <span>Dit spisekammer dækker</span>
                                    <strong>{`−${formatDkkAmount(pantryCoveredValue)}`}</strong>
                                </div>
                            )}
                            <div className="is-total">
                                <span>Du skal købe for</span>
                                <strong>{formatDkkAmount(displayedShoppingTotal)}</strong>
                            </div>
                            {displayedShoppingSavings > 0 && (
                                <div className="is-comparison">
                                    <span>Heraf sparet på tilbud mod normalpris</span>
                                    <strong>{formatDkkAmount(displayedShoppingSavings)}</strong>
                                </div>
                            )}
                            {hasWeeklyBudget && (
                                <div className="is-comparison">
                                    <span>Tilbage i dit ugebudget</span>
                                    <strong>{formatDkkAmount(remainingWeeklyBudget)}</strong>
                                </div>
                            )}
                            <small>
                                Vi trækker både hele varer og delmængder fra, før resten rundes op til pakker.
                            </small>
                        </div>
                    )}
                </div>
                <section className={`home-shopping-list-panel${isShoppingListOpen ? ' is-open' : ''}`}>
                    <div className="home-shopping-list-header">
                        <div>
                            <span className="home-shopping-list-eyebrow">Klar til indkøb</span>
                            <h3>Samlet indkøbsliste</h3>
                            <p>
                                {shoppingListItemCount > 0
                                    ? `${shoppingListItemCount} varer at købe efter fradrag fra dit spisekammer.`
                                    : pantryCoveredShoppingItemCount > 0
                                        ? 'Dit spisekammer dækker hele indkøbslisten.'
                                        : 'Indkøbslisten bliver klar, når madplanen har retter.'}
                            </p>
                        </div>
                        <div className="home-shopping-list-actions">
                            <button
                                type="button"
                                className="home-shopping-list-toggle"
                                onClick={() => setIsShoppingListOpen((isOpen) => !isOpen)}
                                aria-expanded={isShoppingListOpen}
                                aria-controls={`home-plan-shopping-list-${placement}`}
                                disabled={shoppingListItemCount === 0}
                            >
                                {isShoppingListOpen ? 'Skjul liste' : 'Se indkøbsliste'}
                            </button>
                        </div>
                    </div>
                    {(shoppingListShareStatus || shoppingListPantryStatus) && (
                        <div className="home-shopping-list-feedback" role="status" aria-live="polite">
                            {shoppingListPantryStatus || shoppingListShareStatus}
                        </div>
                    )}
                    <div
                        id={`home-plan-shopping-list-${placement}`}
                        className="home-shopping-list-body"
                        aria-hidden={!isShoppingListOpen}
                    >
                        <div className={`home-inventory-mode-picker${isInventoryModeDetailsOpen ? ' is-open' : ''}`}>
                            <div className="home-inventory-mode-summary">
                                <span className="home-inventory-mode-summary-icon" aria-hidden="true">
                                    {inventoryMode === 'smart' ? <LightningCharge /> : <CheckLg />}
                                </span>
                                <span className="home-inventory-mode-summary-copy">
                                    <strong>{inventoryMode === 'smart' ? 'Smart registrering' : 'Præcis registrering'}</strong>
                                    <small>{inventoryMode === 'smart'
                                        ? 'Budgetvenlig holder mængderne ajour, når du bekræfter køb og madlavning.'
                                        : 'Du vælger selv købte varer, forbrug og faktiske rester.'}</small>
                                </span>
                                <button
                                    type="button"
                                    className="home-inventory-mode-change"
                                    onClick={() => setIsInventoryModeDetailsOpen((isOpen) => !isOpen)}
                                    aria-expanded={isInventoryModeDetailsOpen}
                                    aria-controls={`home-inventory-mode-options-${placement}`}
                                >
                                    {isInventoryModeDetailsOpen ? 'Luk' : 'Skift'}
                                </button>
                            </div>
                            {isInventoryModeDetailsOpen && (
                                <div
                                    id={`home-inventory-mode-options-${placement}`}
                                    className="home-inventory-mode-options"
                                    role="group"
                                    aria-label="Vælg registrering af spisekammer"
                                >
                                    <button
                                        type="button"
                                        className={inventoryMode === 'smart' ? 'is-active' : ''}
                                        aria-pressed={inventoryMode === 'smart'}
                                        onClick={() => handleInventoryModeChange('smart')}
                                    >
                                        <span className="home-inventory-mode-option-title">
                                            <LightningCharge size={15} aria-hidden="true" />
                                            <strong>Smart</strong>
                                            <em>Mindst arbejde</em>
                                        </span>
                                        <span>Budgetvenlig beregner køb, forbrug og sandsynlige rester.</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={inventoryMode === 'precise' ? 'is-active' : ''}
                                        aria-pressed={inventoryMode === 'precise'}
                                        onClick={() => handleInventoryModeChange('precise')}
                                    >
                                        <span className="home-inventory-mode-option-title">
                                            <CheckLg size={15} aria-hidden="true" />
                                            <strong>Præcis</strong>
                                            <em>Mest kontrol</em>
                                        </span>
                                        <span>Du markerer selv købte varer, forbrug og faktiske rester.</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className={`home-shopping-pantry-action${inventoryMode === 'smart' ? ' is-smart' : ''}`}>
                            {inventoryMode === 'smart' ? (
                                <>
                                    <span>
                                        <strong>Er hele listen købt?</strong>
                                        <small>Bekræft kun, hvis du købte alt.</small>
                                    </span>
                                    <button
                                        type="button"
                                        className="home-shopping-smart-purchase"
                                        onClick={handleSmartPurchase}
                                        disabled={shoppingListItemCount === 0}
                                    >
                                        <LightningCharge size={14} aria-hidden="true" />
                                        Ja, alt er købt
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span>
                                        <strong>Har du handlet?</strong>
                                        <small>Markér de købte varer nedenfor.</small>
                                    </span>
                                    <span className="home-shopping-pantry-buttons">
                                        <button
                                            type="button"
                                            className="home-shopping-select-all"
                                            onClick={handleToggleAllShoppingItems}
                                            aria-pressed={areAllShoppingItemsChecked}
                                        >
                                            <CheckLg size={14} aria-hidden="true" />
                                            {areAllShoppingItemsChecked ? 'Fjern alle markeringer' : 'Markér alle som købt'}
                                        </button>
                                        <button
                                            type="button"
                                            className="home-shopping-save-pantry"
                                            onClick={handleAddCheckedItemsToPantry}
                                            disabled={checkedShoppingItemCount === 0}
                                        >
                                            <CheckLg size={14} aria-hidden="true" />
                                            {checkedShoppingItemCount > 0
                                                ? `Læg ${checkedShoppingItemCount} i spisekammer`
                                                : 'Vælg købte varer'}
                                        </button>
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="home-shopping-list-utilities" aria-label="Del eller print indkøbslisten">
                            <span>{`${visibleMealCount} ${visibleMealCount === 1 ? 'ret' : 'retter'} · ${shoppingListOfferStoreCount} ${shoppingListOfferStoreCount === 1 ? 'tilbudsbutik' : 'tilbudsbutikker'}`}</span>
                            <span>
                                <button
                                    type="button"
                                    className="home-shopping-list-share"
                                    onClick={handleShareShoppingList}
                                    disabled={shoppingListItemCount === 0}
                                >
                                    <Share size={15} aria-hidden="true" />
                                    Del
                                </button>
                                <button
                                    type="button"
                                    className="home-shopping-list-print"
                                    onClick={() => window.print()}
                                    disabled={shoppingListItemCount === 0}
                                >
                                    <Printer size={15} aria-hidden="true" />
                                    Print
                                </button>
                            </span>
                        </div>
                        <div className="home-shopping-store-grid">
                            {visibleShoppingListGroups.map((group) => {
                                const purchasableGroupItems = group.items;
                                const isWholeStoreChecked = purchasableGroupItems.length > 0
                                    && purchasableGroupItems.every((item) => checkedShoppingItems[item.key]);

                                return (
                                <article key={`shopping-store-${group.key}`} className="home-shopping-store-group">
                                    <header>
                                        <span className="home-shopping-store-logo" aria-hidden="true">
                                            {group.logo
                                                ? <img src={group.logo} alt="" />
                                                : <Shop size={16} />}
                                        </span>
                                        <span className="home-shopping-store-heading">
                                            <strong>{group.name}</strong>
                                            <small>{group.isOfferStore ? 'Valgte tilbudsvarer' : 'Kan købes, hvor det passer dig'}</small>
                                        </span>
                                        <span className="home-shopping-store-controls">
                                            {group.weeklyPriceAmount > 0 && (
                                                <span className="home-shopping-store-total">
                                                <strong>{formatDkkAmount(group.weeklyPriceAmount)}</strong>
                                                {group.savingsAmount > 0 && <small>{`Spar ca. ${formatDkkAmount(group.savingsAmount)}`}</small>}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setShoppingItemsChecked(purchasableGroupItems, !isWholeStoreChecked)}
                                                aria-pressed={isWholeStoreChecked}
                                                disabled={purchasableGroupItems.length === 0}
                                            >
                                                <CheckLg size={11} aria-hidden="true" />
                                                {isWholeStoreChecked ? 'Fjern alle' : 'Markér alle'}
                                            </button>
                                        </span>
                                    </header>
                                    <ul>
                                        {group.items.map((item) => (
                                            <li key={item.key}>
                                                <button
                                                    type="button"
                                                    className={`home-shopping-check${checkedShoppingItems[item.key] ? ' is-checked' : ''}`}
                                                    onClick={() => {
                                                        setShoppingListPantryStatus('');
                                                        setCheckedShoppingItems((previousItems) => ({
                                                            ...previousItems,
                                                            [item.key]: !previousItems[item.key],
                                                        }));
                                                    }}
                                                    aria-pressed={Boolean(checkedShoppingItems[item.key])}
                                                    aria-label={`${checkedShoppingItems[item.key] ? 'Fjern markering fra' : 'Markér som købt'} ${item.name}`}
                                                >
                                                    {checkedShoppingItems[item.key] && <CheckLg size={10} aria-hidden="true" />}
                                                </button>
                                                <span className="home-shopping-item-copy">
                                                    <strong>{item.name}</strong>
                                                    <span>{`Køb ${formatShoppingPackage(item)}`}</span>
                                                    {formatPantryShoppingAdjustment(item) && (
                                                        <small className="home-shopping-pantry-adjustment">
                                                            {formatPantryShoppingAdjustment(item)}
                                                        </small>
                                                    )}
                                                    {item.recipeNames.length > 0 && (
                                                        <small>{`Til: ${item.recipeNames.join(', ')}`}</small>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                                );
                            })}
                        </div>
                        <details className="home-shopping-list-note">
                            <summary>Hvad er ikke med på listen?</summary>
                            <p>Basisvarer, som opskrifterne regner med allerede findes hjemme, er ikke med.</p>
                        </details>
                    </div>
                </section>
                <details className="home-discount-source-panel">
                    <summary>
                        <span>Tilbud pr. butik</span>
                        <strong>{`Spar ${availableDiscountTotal > 0 ? formatDkkAmount(availableDiscountTotal) : '0 kr.'}`}</strong>
                    </summary>
                    {availableDiscountSources.length > 0 ? (
                        <div className="home-discount-source-list" aria-label="Besparelse fordelt på butikker">
                            {availableDiscountSources.map((source) => {
                                const share = Math.max(8, Math.round(((source.savingsAmount || 0) / maxAvailableDiscountSavings) * 100));

                                return (
                                    <div
                                        key={`discount-source-${source.key}`}
                                        className="home-discount-source-item"
                                        style={{ '--store-offer-share': `${share}%` }}
                                        title={`${source.name}: ca. ${source.savingsAmount > 0 ? formatDkkAmount(source.savingsAmount) : '0 kr.'} rabat i planen`}
                                    >
                                        {source.logo ? (
                                            <img src={source.logo} alt="" aria-hidden="true" />
                                        ) : (
                                            <span className="home-discount-source-fallback" aria-hidden="true">
                                                {source.name.slice(0, 1)}
                                            </span>
                                        )}
                                        <span className="home-discount-source-text">
                                            <strong>{source.name}</strong>
                                            <em>{`Spar ${source.savingsAmount > 0 ? formatDkkAmount(source.savingsAmount) : '0 kr.'}`}</em>
                                        </span>
                                        <span className="home-discount-source-meter" aria-hidden="true">
                                            <span />
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="home-discount-source-empty">
                            Ingen tilbudsbutikker er knyttet til retterne endnu.
                        </p>
                    )}
                </details>
                <div className="home-location-context" aria-label="Butikker brugt til forslag">
                    <Shop size={15} aria-hidden="true" />
                    <span>
                        <strong>{searchHasLocation ? `${Number(searchSnapshot.radius).toFixed(1)} km radius` : 'Butikker i madplanen'}</strong>
                        {` · ${locationStoreSummary}`}
                    </span>
                </div>
                <div className="home-search-results-shell">
                    <div
                        className="home-search-results-grid"
                        style={{ '--plan-columns': Math.max(4, visiblePlanCards.length + (showCustomAddCard ? 1 : 0)) }}
                    >
                        {visiblePlanCards.length > 0 ? visiblePlanCards.map((recipe, index) => {
                            const recipeDiscountSources = getRecipeDiscountSources(recipe);
                            const visibleRecipeSources = recipeDiscountSources.slice(0, 2);
                            const extraRecipeSourceCount = Math.max(0, recipeDiscountSources.length - visibleRecipeSources.length);
                            const dishSavingsLabel = formatDishSavingsLabel(recipe);
                            const dishHighlightLabel = dishSavingsLabel ? '' : recipe.nutritionHighlight;

                            return [
                                <article
                                    key={recipe.recipe_id}
                                    className={`home-search-result-card${replaceSlotIndex === index ? ' is-replacing' : ''}${replacementFeedback?.slotIndex === index && replacementFeedback.recipeId === recipe.recipe_id ? ' is-just-replaced' : ''}`}
                                >
                                    <Link
                                        to={`/recipes/${recipe.recipe_id}`}
                                        state={getRecipeLinkState(recipe)}
                                        className="home-search-result-link"
                                    >
                                        <img
                                            src={recipe.image}
                                            alt={recipe.title}
                                            className="home-search-result-image"
                                            data-image-index="0"
                                            data-image-candidates={JSON.stringify(recipe.imageCandidates || [])}
                                            style={{ objectPosition: getRecipeImagePosition(recipe.recipe_id) }}
                                            onError={handleRecipeImageError}
                                        />
                                        <div className="home-search-result-copy">
                                            <div className="home-search-day-chip">{`Ret ${index + 1}`}</div>
                                            <h4>{recipe.title}</h4>
                                            {visibleRecipeSources.length > 0 && (
                                                <div
                                                    className="home-search-store-row"
                                                    aria-label={`Tilbud fra ${recipeDiscountSources.map((source) => source.name).join(', ')}`}
                                                >
                                                    {visibleRecipeSources.map((source) => (
                                                        <span
                                                            key={`card-store-${recipe.recipe_id}-${source.key}`}
                                                            className="home-search-store-pill"
                                                            title={source.savingsAmount > 0 ? `${source.name}: ca. ${formatDkkAmount(source.savingsAmount)} rabat` : source.name}
                                                        >
                                                            {source.logo && <img src={source.logo} alt="" aria-hidden="true" />}
                                                            <span>{source.name}</span>
                                                        </span>
                                                    ))}
                                                    {extraRecipeSourceCount > 0 && (
                                                        <span className="home-search-store-pill is-more">{`+${extraRecipeSourceCount}`}</span>
                                                    )}
                                                </div>
                                            )}
                                            {(recipe.weeklyPriceLabel || dishSavingsLabel) && (
                                                <div className="home-search-result-deal-pair">
                                                    {recipe.weeklyPriceLabel && (
                                                        <span className="home-search-result-deal-price">
                                                            <small>Køb ind for</small>
                                                            <strong>{formatPlannerPriceLabel(recipe)}</strong>
                                                        </span>
                                                    )}
                                                    {dishSavingsLabel && (
                                                        <span className="home-search-result-deal-savings">
                                                            {dishSavingsLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {recipe.timeLabel && (
                                                <p className="home-search-result-meta-compact">
                                                    {recipe.timeLabel}
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                    {dishHighlightLabel && (
                                        <div className="home-search-result-highlight-row">
                                            <p className="home-search-result-highlight">
                                                {dishHighlightLabel}
                                            </p>
                                        </div>
                                    )}
                                    {replacementFeedback?.slotIndex === index && replacementFeedback.recipeId === recipe.recipe_id && (
                                        <div className="home-search-result-feedback" aria-live="polite">
                                            <CheckLg size={13} aria-hidden="true" />
                                            Skiftet
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className={`home-search-replace-button${replaceSlotIndex === index ? ' is-active' : ''}`}
                                        onClick={() =>
                                            setReplaceSlotIndex((previousIndex) =>
                                                previousIndex === index ? null : index
                                            )
                                        }
                                    >
                                        <ArrowRepeat size={13} aria-hidden="true" />
                                        Skift ret
                                    </button>
                                </article>,
                                replaceSlotIndex === index ? renderAlternativePanel(index, 'inline') : null,
                            ];
                        }) : (
                            <p className="home-inline-results-empty">
                                {hasWeeklyBudget && recommendationCards.length > 0
                                    ? `Ingen af de prissatte retter kan føjes til planen uden at overskride dit loft på ${formatDkkAmount(weeklyBudget)}. Hæv loftet på din profil eller vælg færre retter.`
                                    : searchHasLocation && scopedSearchStoreKeys.length === 0
                                    ? 'Vi fandt ingen kendte tilbudsbutikker i radius, så madplanen viser ikke tilbud endnu.'
                                    : 'Forslagene kommer frem her, når der er ugens data at vise.'}
                            </p>
                        )}
                        {showCustomAddCard && (
                            <button
                                type="button"
                                className="home-search-result-card home-search-result-card-add"
                                onClick={() => {
                                    setCustomPlanCount(Math.min(plannerCandidateCards.length, customPlanLength + 1));
                                }}
                                aria-label="Tilføj en ret til planen"
                            >
                                <div className="home-search-day-chip">Mere</div>
                                <div className="home-search-result-add-placeholder" aria-hidden="true">
                                    <span className="home-search-result-add-plus">
                                        <PlusLg size={21} aria-hidden="true" />
                                    </span>
                                </div>
                                <h4>Tilføj ret</h4>
                                <p className="home-search-result-meta-compact">
                                    Ekstra forslag
                                </p>
                            </button>
                        )}
                    </div>
                </div>
                {replaceSlotIndex !== null && renderAlternativePanel(replaceSlotIndex, 'desktop')}
                <p className="home-search-results-meta">
                    {searchSnapshot.address ? `Område: ${searchSnapshot.address}. ` : ''}
                    Radius: {Number(searchSnapshot.radius).toFixed(1)} km.
                    {` Madplan: ${planSummaryLabel}.`}
                    {hasWeeklyBudget ? ` Budgetloft: ${formatDkkAmount(weeklyBudget)}.` : ''}
                    {searchSnapshot.selectedStores.length > 0
                        ? ` Butikker: ${getSelectedStoreNames(searchSnapshot.selectedStores).join(', ')}.`
                        : ' Butikker: ikke valgt.'}
                </p>
            </div>
        );
    };

    return (
        <Container className="home-page">
            <section
                ref={heroRef}
                className={`home-hero-card home-hero-editorial${searchSnapshot ? ' home-hero-has-results' : ''}${isSearchCriteriaPromptVisible ? ' home-hero-needs-search-input' : ''}`}
                style={{
                    '--hero-parallax-y': '0px',
                    '--hero-city-image-url': `url(${process.env.PUBLIC_URL}/images/city.jpg)`,
                    '--hero-pasta-image-url': `url(${process.env.PUBLIC_URL}/images/pasta.png?v=20260321-1838)`,
                }}
            >
                <div className="home-hero-media-layer" aria-hidden="true" />
                <div className="home-hero-atmosphere" aria-hidden="true" />
                <div className="home-hero-content">
                    <div className="home-hero-copy home-hero-copy-editorial">
                        <div className="home-hero-layout">
                            <div className="home-hero-primary">
                                <Row className="section-heading-row home-hero-heading-row">
                                    <Col className={mobileHeroPreviewRecipe ? 'home-hero-heading-col has-mobile-food-proof' : 'home-hero-heading-col'}>
                                        <h3 className="home-hero-main-heading">
                                            <span className="home-hero-heading-desktop">
                                                Vi finder de bedste måltider baseret på ugens tilbud nær dig
                                            </span>
                                            <span className="home-hero-heading-mobile">
                                                Lav madplan fra ugens tilbud
                                            </span>
                                        </h3>
                                        <p className="home-hero-subcopy">
                                            <span className="home-hero-subcopy-desktop">
                                                Spar tid, penge og madspild med opskrifter, der bruger ugens tilbud fra dine foretrukne butikker.
                                            </span>
                                            <span className="home-hero-subcopy-mobile">
                                                Vælg butikkerne. Vi finder retter baseret på ugens tilbud og samler dem i en enkel madplan.
                                            </span>
                                        </p>
                                        {mobileHeroPreviewRecipe && (
                                            <Link
                                                to={`/recipes/${mobileHeroPreviewRecipe.recipe_id}`}
                                                state={getRecipeLinkState(mobileHeroPreviewRecipe)}
                                                className="home-mobile-food-proof"
                                                aria-label={`Eksempel på ret: ${mobileHeroPreviewRecipe.title}`}
                                            >
                                                <img
                                                    src={mobileHeroPreviewRecipe.image}
                                                    alt=""
                                                    data-image-index="0"
                                                    data-image-candidates={JSON.stringify(mobileHeroPreviewRecipe.imageCandidates || [])}
                                                    style={{ objectPosition: getRecipeImagePosition(mobileHeroPreviewRecipe.recipe_id) }}
                                                    onError={handleRecipeImageError}
                                                />
                                                {(mobileHeroPreviewPriceLabel || mobileHeroPreviewSavingsLabel) && (
                                                    <span className="home-mobile-food-proof-deal">
                                                        {mobileHeroPreviewPriceLabel && (
                                                            <span className="home-mobile-food-proof-price">
                                                                <small>Køb ind for</small>
                                                                <strong>{mobileHeroPreviewPriceLabel}</strong>
                                                            </span>
                                                        )}
                                                        {mobileHeroPreviewSavingsLabel && (
                                                            <span className="home-mobile-food-proof-savings">
                                                                {mobileHeroPreviewSavingsLabel}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </Link>
                                        )}
                                    </Col>
                                </Row>
                                <div className="home-hero-interactive">
                                    <div className="home-hero-controls-dock">
                                        <div className={`home-address-panel${isMobileAddressOpen ? ' is-mobile-open' : ''}`}>
                                            <button
                                                type="button"
                                                className="home-mobile-address-toggle"
                                                aria-expanded={isMobileAddressOpen}
                                                onClick={() => setIsMobileAddressOpen((isOpen) => !isOpen)}
                                            >
                                                <GeoAltFill aria-hidden="true" />
                                                <span>Brug adresse</span>
                                            </button>
                                            <div className="home-address-panel-fields">
                                                <AddressSelector
                                                    initialCriteria={savedProfile}
                                                    onCriteriaChange={setSearchCriteria}
                                                    onLocationLookupRequest={runNearbyStoreLookup}
                                                    onAddressConfirmationChange={setIsAddressSuggestionConfirmed}
                                                    onRadiusChange={invalidateNearbyStoreLookup}
                                                />
                                            </div>
                                        </div>
                                        <Row className="home-store-divider">
                                            <Col>
                                                <div className="seperator text-center">eller</div>
                                                <div className="manual-store-hint text-center">Vælg butikker direkte</div>
                                            </Col>
                                        </Row>
                                        {isSearchCriteriaPromptVisible && (
                                            <div
                                                id="home-search-criteria-prompt"
                                                className="home-search-criteria-prompt"
                                                role="alert"
                                            >
                                                <span className="home-search-criteria-prompt-icon" aria-hidden="true">
                                                    <Shop />
                                                </span>
                                                <span>
                                                    <strong>Vælg først, hvor vi skal lede</strong>
                                                    <span>Vælg mindst én butik nedenfor, eller indtast din adresse ovenfor. Klik derefter på “Find mine retter”.</span>
                                                </span>
                                            </div>
                                        )}
                                        <div className="home-mobile-store-selector" aria-label="Vælg butikker">
                                            <p className="home-mobile-store-helper">
                                                <span className="home-mobile-store-helper-row">
                                                    <strong>Hvor handler du?</strong>
                                                    <span className="home-mobile-store-count">{mobileStoreSelectionLabel}</span>
                                                </span>
                                                <span>Vi bruger tilbud fra de valgte butikker</span>
                                            </p>
                                            <div className="home-mobile-store-chip-grid">
                                                {mobileStoreKeys.map((storeKey) => renderMobileStoreButton(storeKey))}
                                            </div>
                                        </div>
                                        {nearbyStorePreview && (
                                            <p
                                                className={`home-nearby-store-preview is-${nearbyStorePreview.status}`}
                                                role="status"
                                                aria-live="polite"
                                            >
                                                <span className="home-nearby-store-preview-icon" aria-hidden="true">
                                                    {nearbyStorePreview.status === 'success' ? <CheckLg /> : <GeoAltFill />}
                                                </span>
                                                <span>{nearbyStorePreview.message}</span>
                                            </p>
                                        )}
                                        <div className="home-hero-store-panel">
                                            <StoreSelectorCollection
                                                direction="horizontal"
                                                enabledStoreKeys={shouldRestrictStoreButtons ? activeNearbyStoreKeys : null}
                                                highlightedStoreKeys={selectedNearbyStoreKeys}
                                            />
                                        </div>
                                        <div className="home-search-actions">
                                            <button
                                                type="button"
                                                className={`home-search-button${isAddressSearchReady ? ' is-address-ready' : ''}${isSearchCriteriaPromptVisible ? ' is-missing-criteria' : ''}`}
                                                aria-expanded={Boolean(searchSnapshot)}
                                                aria-describedby={isSearchCriteriaPromptVisible ? 'home-search-criteria-prompt' : undefined}
                                                onClick={openResponsiveResults}
                                            >
                                                <span className="home-search-button-label">
                                                    {isSearchCriteriaPromptVisible
                                                        ? 'Vælg adresse eller butikker først'
                                                        : 'Find mine retter'}
                                                </span>
                                                {isAddressSearchReady && (
                                                    <span className="home-search-button-cue" aria-hidden="true">
                                                        <span>Klar</span>
                                                        <ArrowRight />
                                                    </span>
                                                )}
                                            </button>
                                            {homepageDataStatus !== 'ready' ? (
                                                <p className={`home-data-status is-${homepageDataStatus}`} role="status">
                                                    {homepageDataStatus === 'loading'
                                                        ? 'Henter ugens forslag...'
                                                        : 'Forslag kunne ikke hentes lige nu.'}
                                                </p>
                                            ) : homepageDataNotice ? (
                                                <p className="home-data-status is-notice" role="status">
                                                    {homepageDataNotice}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>

                                </div>
                            </div>
                            {searchSnapshot ? (
                                <aside className="home-hero-location-map" aria-label="Lokation og butikker i radius">
                                    <div className={`home-hero-map-surface is-${searchSnapshot.locationStatus || 'idle'}`}>
                                        <div className="home-hero-map-streets" aria-hidden="true" />
                                        <div className="home-hero-map-radar" aria-hidden="true">
                                            <span className="home-hero-map-ring home-hero-map-ring-one" />
                                            <span className="home-hero-map-ring home-hero-map-ring-two" />
                                            <span className="home-hero-map-sweep" />
                                            <span className="home-hero-map-core" />
                                        </div>
                                        {heroMapStoreKeys.map((storeKey, index) => (
                                            <div
                                                key={`hero-map-pin-${storeKey}`}
                                                className={`home-hero-map-pin home-hero-map-pin-${['one', 'two', 'three', 'four', 'five', 'six'][index] || 'six'}`}
                                            >
                                                {getHeroMapStoreLabel(storeKey)}
                                            </div>
                                        ))}
                                        <div className="home-hero-map-card">
                                            <span>
                                                {searchSnapshot.locationStatus === 'loading'
                                                    ? 'Scanner radius'
                                                    : 'Radius aktiv'}
                                            </span>
                                            <strong>{Number(searchSnapshot.radius).toFixed(1)} km</strong>
                                            <p>
                                                {searchSnapshot.locationStatus === 'loading'
                                                    ? 'Finder butikker nær dig'
                                                    : heroMapStoreSummary
                                                        ? `Madplan: ${heroMapStoreSummary}`
                                                        : searchSnapshot.locationStatus === 'empty'
                                                            ? 'Ingen butikker fundet i radius'
                                                            : searchSnapshot.address || 'Butikker nær dig'}
                                            </p>
                                        </div>
                                    </div>
                                </aside>
                            ) : activeHeroRecipe && (
                                <aside className="home-hero-featured home-hero-plan-preview" aria-label="Madplan baseret på ugens tilbud">
                                    <Link
                                        to={`/recipes/${activeHeroRecipe.recipe_id}`}
                                        state={getRecipeLinkState(activeHeroRecipe)}
                                        className="home-hero-featured-link home-hero-plan-showcase"
                                    >
                                        <img
                                            key={activeHeroRecipe.recipe_id}
                                            src={activeHeroRecipe.image}
                                            alt={activeHeroRecipe.title}
                                            className="home-hero-featured-image"
                                            data-image-index="0"
                                            data-image-candidates={JSON.stringify(activeHeroRecipe.imageCandidates || [])}
                                            style={{ objectPosition: getRecipeImagePosition(activeHeroRecipe.recipe_id) }}
                                            onError={handleRecipeImageError}
                                        />
                                        <div className="home-hero-discount-flag">
                                            <span>Du sparer</span>
                                            <strong>{activeHeroSavingsAmountLabel || 'på tilbud'}</strong>
                                        </div>
                                        {(activeHeroStoreMeta.name || activeHeroStoreMeta.logo) && (
                                            <div className="home-hero-source-chip">
                                                {activeHeroStoreMeta.logo && (
                                                    <img src={activeHeroStoreMeta.logo} alt="" aria-hidden="true" />
                                                )}
                                                <span>{activeHeroStoreMeta.name || 'Ugens tilbud'}</span>
                                            </div>
                                        )}
                                        <div className="home-hero-featured-card home-hero-plan-card">
                                            <span className="home-hero-featured-kicker">{`Ret ${activeHeroRecipeDisplayIndex + 1} i madplanen`}</span>
                                            <h4>{activeHeroRecipe.title}</h4>
                                            <p className="home-hero-plan-card-meta">
                                                {[activeHeroPriceLabel, activeHeroRecipe.timeLabel].filter(Boolean).join(' · ') || 'Udvalgt fra ugens tilbud'}
                                            </p>
                                        </div>
                                    </Link>
                                    <div className="home-hero-week-plan" aria-label="Eksempel på en fuld uge med tilbudsretter">
                                        <div className="home-hero-week-plan-header">
                                            <div className="home-hero-week-plan-title">
                                                <span>Madplanen bygges op</span>
                                                <strong>{`${heroCollectedPlanCards.length} af ${MAX_PLAN_LENGTH} retter samlet`}</strong>
                                            </div>
                                            <div className="home-hero-week-totals" aria-live="polite">
                                                <div className="home-hero-week-total-card">
                                                    <span>Ugens indkøb</span>
                                                    <strong
                                                        key={`hero-total-price-${Math.round(heroCollectedPlanTotals.totalPrice)}`}
                                                        className="home-hero-week-total-value"
                                                    >
                                                        {heroCollectedPlanTotals.totalPrice > 0 ? formatDkkAmount(heroCollectedPlanTotals.totalPrice) : '0 kr.'}
                                                    </strong>
                                                </div>
                                                <div className="home-hero-week-total-card is-savings">
                                                    <span>Du sparer ca.</span>
                                                    <strong
                                                        key={`hero-total-savings-${Math.round(heroCollectedPlanTotals.totalSavings)}`}
                                                        className="home-hero-week-total-value"
                                                    >
                                                        {heroCollectedPlanTotals.totalSavings > 0 ? formatDkkAmount(heroCollectedPlanTotals.totalSavings) : '0 kr.'}
                                                    </strong>
                                                </div>
                                            </div>
                                        </div>
                                        {heroCollectedStoreSummary && (
                                            <p className="home-hero-week-plan-source">{`Senest samlet fra ${heroCollectedStoreSummary}`}</p>
                                        )}
                                        {heroCollectedPlanCards.length > 0 && (
                                            <div className="home-hero-week-plan-rail">
                                                {heroCollectedPlanCards.map((recipe, index) => {
                                                    const savingsLabel = formatDishSavingsLabel(recipe);

                                                    return (
                                                        <Link
                                                            key={`hero-plan-${recipe.recipe_id}`}
                                                            to={`/recipes/${recipe.recipe_id}`}
                                                            state={getRecipeLinkState(recipe)}
                                                            className="home-hero-plan-slot is-collected"
                                                            aria-label={`Ret ${index + 1}: ${recipe.title}`}
                                                        >
                                                            <img
                                                                src={recipe.image}
                                                                alt=""
                                                                className="home-hero-plan-slot-image"
                                                                data-image-index="0"
                                                                data-image-candidates={JSON.stringify(recipe.imageCandidates || [])}
                                                                style={{ objectPosition: getRecipeImagePosition(recipe.recipe_id) }}
                                                                onError={handleRecipeImageError}
                                                            />
                                                            <span className="home-hero-plan-slot-day">{`Ret ${index + 1}`}</span>
                                                            <span className="home-hero-plan-slot-title">{recipe.title}</span>
                                                            <span className="home-hero-plan-slot-saving">
                                                                {savingsLabel || formatPlannerPriceLabel(recipe) || 'Tilbud'}
                                                            </span>
                                                        </Link>
                                                    );
                                                })}
                                                {heroCollectedPlanCards.length < MAX_PLAN_LENGTH && (
                                                    <div
                                                        className="home-hero-plan-slot home-hero-plan-slot-add"
                                                        aria-hidden="true"
                                                    >
                                                        <span className="home-hero-plan-slot-day">Mere</span>
                                                        <span className="home-hero-plan-add-icon">
                                                            <PlusLg size={18} aria-hidden="true" />
                                                        </span>
                                                        <span className="home-hero-plan-add-title">Tilføj ret</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </aside>
                            )}
                        </div>
                        {renderSuggestionPanel('hero')}
                    </div>
                </div>
            </section>

            <div className="home-section-route-stack home-intro-story">
                {searchSnapshot ? (
                    renderSuggestionPanel('route')
                ) : (
                    <section className="home-info-section home-section-route home-flow-section">
                        <div className="home-feature-section-heading is-centered">
                            <span className="home-feature-eyebrow">Hele kredsløbet</span>
                            <h2>Fra ugens tilbud til mindre madspild</h2>
                            <p className="home-feature-lead">
                                Budgetvenlig holder styr på sammenhængen, så du kun skal tage stilling til næste skridt.
                            </p>
                        </div>
                        <div className="home-flow-track" aria-label="Madplan, indkøb, madlavning og rester">
                            <span>Planlæg</span>
                            <ArrowRight aria-hidden="true" />
                            <span>Køb det manglende</span>
                            <ArrowRight aria-hidden="true" />
                            <span>Lav maden</span>
                            <ArrowRight aria-hidden="true" />
                            <span>Gem resten</span>
                        </div>
                        <ol className="home-flow-steps">
                            {HOW_IT_WORKS_STEPS.map((step, index) => (
                                <li key={step.title} className="home-flow-step">
                                    <span className="home-flow-step-number" aria-hidden="true">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className="home-flow-step-media" aria-hidden="true">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/how_it_works/${step.icon}`}
                                            alt=""
                                            className="home-flow-step-icon"
                                        />
                                    </span>
                                    <span className="home-flow-step-copy">
                                        <strong>{step.title}</strong>
                                        <p>{step.text}</p>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                <section className="home-info-section home-section-route home-example-section">
                    <h2>Eksempel fra denne uge</h2>
                    {activeHeroRecipe ? (
                        <article className="home-example-featured">
                            <Link
                                to={`/recipes/${activeHeroRecipe.recipe_id}`}
                                state={getRecipeLinkState(activeHeroRecipe)}
                                className="home-example-featured-link"
                            >
                                <img
                                    key={`example-${activeHeroRecipe.recipe_id}`}
                                    src={activeHeroRecipe.image}
                                    alt={activeHeroRecipe.title}
                                    className="home-example-featured-image"
                                    data-image-index="0"
                                    data-image-candidates={JSON.stringify(activeHeroRecipe.imageCandidates || [])}
                                    style={{ objectPosition: getRecipeImagePosition(activeHeroRecipe.recipe_id) }}
                                    onError={handleRecipeImageError}
                                />
                                <div className="home-example-featured-content">
                                    <span className="home-example-featured-kicker">Ugens forslag</span>
                                    <h4>{activeHeroRecipe.title}</h4>
                                    <p>
                                        {activeHeroRecipe.weeklyOfferSummary
                                            || activeHeroRecipe.weeklySavingsLabel
                                            || activeHeroRecipe.nutritionHighlight
                                            || 'En aktuel hverdagsret fra opskrifterne.'}
                                    </p>
                                    <span className="home-example-featured-meta">
                                        {[activeHeroRecipe.weeklyPriceLabel, activeHeroRecipe.timeLabel].filter(Boolean).join(' · ') || 'Se opskrift'}
                                    </span>
                                </div>
                            </Link>
                            {heroRecipeCards.length > 1 && (
                                <div className="home-example-featured-dots" aria-hidden="true">
                                    {heroRecipeCards.map((recipe, index) => (
                                        <span
                                            key={recipe.recipe_id}
                                            className={`home-example-featured-dot${index === activeHeroRecipeDisplayIndex ? ' is-active' : ''}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </article>
                    ) : (
                        <p className="home-example-empty">Ugens forslag vises her, når opskriftsdata er hentet.</p>
                    )}
                </section>

                <section className="home-info-section home-section-route home-modes-section">
                    <div className="home-feature-section-heading is-centered">
                        <span className="home-feature-eyebrow">Dit valg</span>
                        <h2>Vælg hvordan du vil spare</h2>
                        <p className="home-feature-lead home-modes-intro">
                            Saml indkøbet ét sted, eller lad tilbuddene bestemme ruten. Du ser forskellen, før du vælger.
                        </p>
                    </div>
                    <Row className="home-modes-row">
                        <Col md={6}>
                            <div className="home-mode-card is-convenient">
                                <span className="home-mode-badge">Færrest stop</span>
                                <div className="home-mode-icon-wrap" aria-hidden="true">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/how-to-save/samletkoeb.png`}
                                        alt=""
                                        className="home-mode-icon"
                                    />
                                </div>
                                <div className="home-mode-content">
                                    <h4>Samlet køb</h4>
                                    <p className="home-mode-summary">Til dig, der vil gøre indkøbet nemt.</p>
                                    <ul className="home-mode-benefits">
                                        <li>én butik</li>
                                        <li>færre stop</li>
                                        <li>hurtigere indkøb</li>
                                    </ul>
                                    <span className="home-mode-fit">Når tiden er vigtigst</span>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="home-mode-card is-saver">
                                <span className="home-mode-badge">Størst besparelse</span>
                                <div className="home-mode-icon-wrap" aria-hidden="true">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/how-to-save/butiksrunde.png`}
                                        alt=""
                                        className="home-mode-icon"
                                    />
                                </div>
                                <div className="home-mode-content">
                                    <h4>Butiksrunde</h4>
                                    <p className="home-mode-summary">Til dig, der vil spare mest muligt.</p>
                                    <ul className="home-mode-benefits">
                                        <li>laveste pris</li>
                                        <li>flere butikker</li>
                                        <li>udnyt tilbud bedst</li>
                                    </ul>
                                    <span className="home-mode-fit">Når prisen er vigtigst</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </section>

                <section className="home-info-section home-section-route home-cookbook-section">
                    <div className="home-cookbook-layout">
                        <div className="home-cookbook-media">
                            <img
                                src={`${process.env.PUBLIC_URL}/images/simple-dishes/cooking.png?v=20260614-1043`}
                                alt="Madlavning på komfur"
                                className="home-cookbook-image"
                            />
                            <div className="home-cookbook-media-caption">
                                <span>Bygget til hverdagen</span>
                                <strong>Færre særindkøb</strong>
                            </div>
                        </div>
                        <div className="home-cookbook-content">
                            <span className="home-feature-eyebrow">Opskrifterne</span>
                            <h2>Opskrifter uden madplan-kaos</h2>
                            <p className="home-cookbook-intro">
                                Vi gør det nemt at lave god hverdagsmad uden lange indkøbslister og uden madspild.
                            </p>
                            <div className="home-cookbook-features">
                                <article className="home-cookbook-feature">
                                    <div className="home-cookbook-icon-wrap">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/simple-dishes/samletkoeb.png`}
                                            alt=""
                                            className="home-cookbook-icon"
                                        />
                                    </div>
                                    <h4>Få ingredienser</h4>
                                    <p>Retter bygget til hverdag, ikke lange specialindkøb.</p>
                                </article>
                                <article className="home-cookbook-feature">
                                    <div className="home-cookbook-icon-wrap">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/simple-dishes/udskiftelige.png`}
                                            alt=""
                                            className="home-cookbook-icon"
                                        />
                                    </div>
                                    <h4>Fleksible erstatninger</h4>
                                    <p>Brug det, du allerede har, eller vælg billigere alternativer.</p>
                                </article>
                                <article className="home-cookbook-feature">
                                    <div className="home-cookbook-icon-wrap">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/simple-dishes/tilbud.png`}
                                            alt=""
                                            className="home-cookbook-icon"
                                        />
                                    </div>
                                    <h4>Tilbudsvarer først</h4>
                                    <p>Opskrifterne tager udgangspunkt i ugens varer på tilbud.</p>
                                </article>
                            </div>
                            <div className="home-cookbook-note">
                                <span aria-hidden="true">✓</span>
                                <p>God mad. Mindre spild. Mere overskud i hverdagen.</p>
                                <Link to="/recipes" className="home-cookbook-link">Se opskrifter</Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="home-info-section home-section-route home-weekly-plan-section">
                    <div className="home-weekly-plan-copy">
                        <span className="home-feature-eyebrow">01 · Madplanen</span>
                        <h2>Én plan bliver til én indkøbsliste</h2>
                        <p className="home-feature-lead">
                            Start med de retter og butikker, der passer til din uge. Vi samler resten for dig.
                        </p>
                        <ul className="home-feature-checklist">
                            <li>Ingredienser fra alle valgte retter samles.</li>
                            <li>Det, du allerede har hjemme, trækkes fra.</li>
                            <li>Kun det manglende rundes op til hele pakker.</li>
                        </ul>
                    </div>
                    <div className="home-weekly-plan-visual" aria-label="Eksempel på tre retter samlet i én indkøbsliste">
                        <div className="home-weekly-plan-visual-header">
                            <span>Din uge</span>
                            <strong>3 retter valgt</strong>
                        </div>
                        <div className="home-weekly-plan-meal">
                            <span>Man</span>
                            <strong>Frikadeller med kartofler</strong>
                            <CheckLg aria-hidden="true" />
                        </div>
                        <div className="home-weekly-plan-meal">
                            <span>Ons</span>
                            <strong>Kylling i karry</strong>
                            <CheckLg aria-hidden="true" />
                        </div>
                        <div className="home-weekly-plan-meal">
                            <span>Fre</span>
                            <strong>Pasta med grønt</strong>
                            <CheckLg aria-hidden="true" />
                        </div>
                        <div className="home-weekly-plan-result">
                            <span className="home-weekly-plan-result-icon" aria-hidden="true">
                                <Shop />
                            </span>
                            <span>
                                <small>Resultat</small>
                                <strong>Én samlet indkøbsliste</strong>
                            </span>
                        </div>
                    </div>
                </section>

                <section className="home-info-section home-section-route home-pantry-section">
                    <div className="home-pantry-copy">
                        <span className="home-feature-eyebrow">02 · Spisekammeret</span>
                        <h2>Køb kun det, der faktisk mangler</h2>
                        <p className="home-pantry-intro">
                            Det, du har hjemme, trækkes fra før indkøbet rundes op. Med Smart registrering
                            opdateres lageret, når du bekræfter køb og madlavning.
                        </p>
                    </div>
                    <div className="home-pantry-demo">
                        <div className="home-pantry-demo-equation">
                            <p className="home-pantry-label">Eksempel i indkøbslisten</p>
                            <div className="home-pantry-quantity-equation">
                                <span>
                                    <small>Behov</small>
                                    <strong>3</strong>
                                    <em>poser løg</em>
                                </span>
                                <b aria-hidden="true">−</b>
                                <span>
                                    <small>Hjemme</small>
                                    <strong>2</strong>
                                    <em>poser løg</em>
                                </span>
                                <b aria-hidden="true">=</b>
                                <span className="is-result">
                                    <small>Indkøb</small>
                                    <strong>1</strong>
                                    <em>pose løg</em>
                                </span>
                            </div>
                        </div>
                        <div className="home-pantry-demo-right">
                            <p className="home-pantry-label">Efter indkøb og madlavning</p>
                            <span className="home-pantry-demo-check" aria-hidden="true"><CheckLg /></span>
                            <span className="home-pantry-demo-result-copy">
                                <strong>Spisekammeret opdateres</strong>
                                <span>Køb lægges til, brug trækkes fra, og faktiske rester gemmes til næste madplan.</span>
                            </span>
                        </div>
                    </div>
                    <p className="home-pantry-support">
                        <strong>Du beholder kontrollen:</strong> Estimerede mængder vises tydeligt og kan altid rettes.
                    </p>
                    <div className="home-pantry-actions">
                        <Link to="/pantry" className="btn home-action-primary">Åbn dit spisekammer</Link>
                        <Link to="/recipes" className="home-pantry-secondary-link">Find opskrifter</Link>
                    </div>
                </section>
                <section className="home-info-section home-section-route home-purpose-cta-section">
                    <span className="home-feature-eyebrow">Det større billede</span>
                    <h2>Hvorfor Budgetvenlig?</h2>
                    <p>
                        Vil du læse mere om tanken bag Budgetvenlig og hvordan vi forbinder tilbud, retter, indkøb og rester
                        i én samlet proces?
                    </p>
                    <Link to="/vision" className="home-purpose-link">
                        <span>Læs vores vision</span>
                        <ArrowRight aria-hidden="true" />
                    </Link>
                </section>
            </div>
        </Container>
    );
}

export default Home;
